import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Collapse, Descriptions, Empty, Input, Modal, Row, Space, Spin, Statistic, Table, Tabs, Tag, Select, Upload, message } from 'antd';
import { CheckCircleOutlined, DownloadOutlined, FileExcelOutlined, FileTextOutlined, InboxOutlined, SafetyOutlined, SearchOutlined, SnippetsOutlined } from '@ant-design/icons';
import axios from 'axios';
import { apiUrl } from '../../../utility/Connections';
import AreaReport from '../managerialCrossGeneral/AreaReport';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
    ReferenceLine, ReferenceArea, LabelList, CartesianGrid,
    AreaChart, Area,
} from 'recharts';

import './ManagerialCrossDashboard.css';
import ManagerialCrossGeneral from '../managerialCrossGeneral/managerialCrossGeneral';
import ManCrossKpiCard from '../managerialCrossGeneral/components/ManCrossKpiCard';
import '../managerialCrossGeneral/managerialCrossGeneral.css';
import {
    computeIvcLevel,
    countBelowThreshold,
    formatPercent,
    formatRounded,
    getCoveragePercent,
    getRiskLabel,
    getStatusTone,
    KPI_TONE_STYLES,
    SUMMARY_SEMAPHORE_ROWS,
    toDimensionRows,
} from '../managerialCrossGeneral/shared/managerialUtils';

// ─── Dimension bar chart (horizontal recharts) ────────────────────────────────
const DIM_COLORS = {
    ipra: { ok: '#62C190', warn: '#D4AC0D', bad: '#F48732', critical: '#F3202A', threshold: 75 },
    iash: { ok: '#62C190', warn: '#D4AC0D', bad: '#F48732', critical: '#F3202A', threshold: 65 },
};

const getDimBarColor = (value, instrument) => {
    const cfg = DIM_COLORS[instrument] || DIM_COLORS.ipra;
    const n = Number(value);
    if (!Number.isFinite(n)) return '#94a3b8';
    if (instrument === 'ipra') {
        if (n >= 85) return cfg.ok;
        if (n >= 75) return cfg.warn;
        if (n >= 65) return cfg.bad;
        return cfg.critical;
    }
    // iash
    if (n >= 80) return cfg.ok;
    if (n >= 65) return cfg.warn;
    if (n >= 50) return cfg.bad;
    return cfg.critical;
};

const RANGE_ZONES = {
    ipra: [
        { x1: 0,  x2: 65, fill: '#E74C3C' },
        { x1: 65, x2: 75, fill: '#E67E22' },
        { x1: 75, x2: 85, fill: '#D4AC0D' },
        { x1: 85, x2: 100, fill: '#62C190' },
    ],
    iash: [
        { x1: 0,  x2: 50, fill: '#E74C3C' },
        { x1: 50, x2: 65, fill: '#E67E22' },
        { x1: 65, x2: 80, fill: '#D4AC0D' },
        { x1: 80, x2: 100, fill: '#62C190' },
    ],
};

// ── Per-area Paso M helpers ────────────────────────────────────────────────────
const irhAvgColor = (v) => {
    if (v == null) return '#9AA2AE';
    if (v < 50) return '#E74C3C';
    if (v < 60) return '#E67E22';
    if (v < 75) return '#D4AC0D';
    return '#62C190';
};

const GRUPO_DASH_COLOR = { A: 'red', B: 'orange', C: 'purple', MIXTO: 'default' };
const GRUPO_DASH_DESC = {
    A:     'El área opera con fatiga acumulada y hábitos inseguros normalizados (Riesgo Físico / Conductual).',
    B:     'El área enfrenta estrés crónico y déficit en comunicación de riesgos (Riesgo Cognitivo / Emocional).',
    C:     'El área presenta bajo reporte y aislamiento cultural (Riesgo Social / Cultural).',
    MIXTO: 'Perfil mixto — intervención diferenciada por trabajador.',
};

const CF_CRITICAL = [
    { key: 'triple', label: 'Triple co-falla (D1+D2+D5)',          bg: '#C0392B', mult: '4.2×' },
    { key: 'dual',   label: 'Co-falla D1+D5 (Bienestar+Disciplina)', bg: '#E74C3C', mult: '3.1×' },
    { key: 'd1',     label: 'D1 Bienestar Físico aislado',           bg: '#E67E22', mult: '2.3×' },
];

const PROFILE_COLORS_DB = {
    'Fatigado':       '#C0392B',
    'Impulsivo':      '#E74C3C',
    'Indisciplinado': '#E67E22',
    'Silencioso':     '#F48732',
    'Aislado':        '#F9A825',
    'Distraído':      '#D4AC0D',
    'Desconectado':   '#1E8449',
};
const PROFILE_ORDER_DB = ['Fatigado','Impulsivo','Indisciplinado','Silencioso','Aislado','Distraído','Desconectado'];

// ─── DimensionLineChart — área/línea por dimensión estilo Power BI ─────────────────────
const DimensionLineChart = ({ rows = [], instrument = 'ipra' }) => {
    if (!rows.length) return null;

    const threshold = DIM_COLORS[instrument]?.threshold || 75;

    const chartData = [...rows]
        .sort((a, b) => {
            const aNum = parseInt((a.dimension || '').match(/^D(\d+)/)?.[1] || '99', 10);
            const bNum = parseInt((b.dimension || '').match(/^D(\d+)/)?.[1] || '99', 10);
            return aNum - bNum;
        })
        .map((r) => ({
            code:     (r.dimension || '').match(/^(D\d+)/)?.[1] || '',
            fullName: r.dimension || '',
            value:    r.average != null ? Math.round(r.average * 10) / 10 : null,
        }));

    const vals = chartData.map((d) => d.value).filter((v) => v != null);
    const yMin = vals.length ? Math.max(0, Math.floor((Math.min(...vals) - 10) / 5) * 5) : 0;

    const CustomDot = (props) => {
        const { cx, cy, payload } = props;
        if (payload.value == null || !Number.isFinite(cx)) return null;
        return (
            <circle cx={cx} cy={cy} r={6}
                    fill={getDimBarColor(payload.value, instrument)}
                    stroke="#fff" strokeWidth={2} />
        );
    };

    const CustomLabel = ({ x, y, value }) => {
        if (value == null) return null;
        return (
            <text x={x} y={y - 12} textAnchor="middle"
                  fill={getDimBarColor(value, instrument)}
                  fontSize={11} fontWeight={800}>
                {value}
            </text>
        );
    };

    const DLTooltip = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const v = payload[0]?.value;
        const d = payload[0]?.payload;
        return (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', padding: '8px 14px',
                          borderRadius: 8, boxShadow: '0 4px 16px rgba(48,56,142,0.12)' }}>
                <div style={{ fontWeight: 700, fontSize: 11, color: '#334155', marginBottom: 4 }}>
                    {d?.fullName}
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: getDimBarColor(v, instrument), lineHeight: 1 }}>
                    {v}
                </div>
                <div style={{ fontSize: 10, color: '#617993', marginTop: 3 }}>
                    Umbral {instrument.toUpperCase()}: {threshold}
                </div>
            </div>
        );
    };

    return (
        <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={chartData}
                       margin={{ top: 34, right: 24, bottom: 4, left: 0 }}>
                <defs>
                    <linearGradient id={`dlg-${instrument}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"   stopColor="#30388e" stopOpacity={0.18} />
                        <stop offset="100%" stopColor="#30388e" stopOpacity={0.02} />
                    </linearGradient>
                </defs>
                <CartesianGrid stroke="#e8eef5" strokeDasharray="2 4" vertical={false} />
                <XAxis
                    dataKey="code"
                    tick={{ fontSize: 11, fill: '#334155', fontWeight: 700 }}
                    axisLine={false}
                    tickLine={false}
                />
                <YAxis
                    domain={[yMin, 100]}
                    tick={{ fontSize: 9, fill: '#8c8c8c' }}
                    axisLine={false}
                    tickLine={false}
                    width={28}
                />
                <Tooltip content={<DLTooltip />} />
                <ReferenceLine
                    y={threshold}
                    stroke="#334155"
                    strokeWidth={1.5}
                    strokeDasharray="4 2"
                    label={{ value: `Umbral ${threshold}`, position: 'insideTopRight',
                             fontSize: 9, fill: '#334155' }}
                />
                <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#30388e"
                    strokeWidth={2.5}
                    fill={`url(#dlg-${instrument})`}
                    dot={<CustomDot />}
                    activeDot={{ r: 8, fill: '#30388e' }}
                    isAnimationActive={false}
                    label={<CustomLabel />}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
};

// ─── BreakdownBarChart — gráfico de barras genérico para filas {name, average, count} ──
const BreakdownBarChart = ({ rows = [], instrument = 'iash', maxItems = 20, selectedName, onSelect }) => {
    if (!rows.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin datos" style={{ padding: '16px 0' }} />;

    const threshold = DIM_COLORS[instrument]?.threshold || 65;
    const zones     = RANGE_ZONES[instrument] || RANGE_ZONES.iash;

    const chartData = (maxItems ? rows.slice(0, maxItems) : rows).map((r) => ({
        name:     (r.name || '').length > 30 ? (r.name || '').substring(0, 28) + '\u2026' : (r.name || ''),
        fullName: r.name,
        value:    r.average != null ? Math.round(r.average * 10) / 10 : 0,
        count:    r.count,
    }));

    const renderValueLabel = ({ x, y, width, height, value }) => {
        const cx = (x || 0) + (width || 0);
        const cy = (y || 0) + (height || 0) / 2;
        return (
            <text x={cx + 8} y={cy} fill="#334155" fontSize={11} fontWeight={800}
                  dominantBaseline="middle" textAnchor="start">
                {value}
            </text>
        );
    };

    const CustomTooltipBD = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const v   = payload[0]?.value;
        const cnt = payload[0]?.payload?.count;
        return (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', padding: '10px 14px',
                          borderRadius: 8, boxShadow: '0 4px 16px rgba(48,56,142,0.12)' }}>
                <div style={{ fontWeight: 700, fontSize: 12, color: '#334155', marginBottom: 6 }}>
                    {payload[0]?.payload?.fullName}
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: getDimBarColor(v, instrument), lineHeight: 1 }}>
                    {v}
                </div>
                {cnt != null && (
                    <div style={{ fontSize: 11, color: '#617993', marginTop: 3 }}>
                        {cnt} evaluado{cnt !== 1 ? 's' : ''}
                    </div>
                )}
                <div style={{ fontSize: 11, color: '#617993', marginTop: 2 }}>
                    Umbral {instrument.toUpperCase()}: {threshold}
                </div>
            </div>
        );
    };

    const barSize = 14;
    const height  = Math.max(140, chartData.length * (barSize + 20) + 50);

    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart data={chartData} layout="vertical"
                      margin={{ top: 6, right: 52, bottom: 4, left: 8 }}
                      onClick={onSelect ? (data) => {
                          const name = data?.activePayload?.[0]?.payload?.fullName;
                          if (name) onSelect(name);
                      } : undefined}>
                {zones.map((z) => (
                    <ReferenceArea key={z.x1} x1={z.x1} x2={z.x2} fill={z.fill} fillOpacity={0.05} />
                ))}
                <CartesianGrid stroke="#e8eef5" horizontal={false} vertical={true} strokeDasharray="2 4" />
                <XAxis
                    type="number"
                    domain={[0, 100]}
                    ticks={instrument === 'ipra' ? [0, 65, 75, 85, 100] : [0, 50, 65, 80, 100]}
                    tick={{ fontSize: 9, fill: '#8c8c8c' }}
                    axisLine={false}
                    tickLine={false}
                    orientation="top"
                />
                <YAxis
                    type="category"
                    dataKey="name"
                    width={170}
                    interval={0}
                    tick={{ fontSize: 11, fill: '#334155' }}
                    axisLine={false}
                    tickLine={false}
                />
                <Tooltip content={<CustomTooltipBD />} />
                <ReferenceLine x={threshold} stroke="#334155" strokeWidth={2} />
                <Bar dataKey="value" barSize={barSize} radius={[0, 6, 6, 0]} isAnimationActive={false}
                     style={onSelect ? { cursor: 'pointer' } : undefined}>
                    {chartData.map((entry, index) => (
                        <Cell
                            key={`cell-${index}`}
                            fill={getDimBarColor(entry.value, instrument)}
                            opacity={selectedName && entry.fullName !== selectedName ? 0.35 : 1}
                        />
                    ))}
                    <LabelList dataKey="value" content={renderValueLabel} />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

// ─── NPS helpers & NpsBarChart ────────────────────────────────────────────────
const _npsFromEmps = (emps, groupKey) => {
    const map = {};
    for (const e of emps) {
        const grp = e[groupKey];
        if (!grp) continue;
        map[grp] = map[grp] || { p: 0, n: 0, d: 0 };
        map[grp].p += e.promoters || 0;
        map[grp].n += e.passives || 0;
        map[grp].d += e.detractors || 0;
    }
    return Object.entries(map).map(([name, c]) => {
        const tot = c.p + c.n + c.d;
        if (!tot) return null;
        return { name, average: Math.round((c.p / tot - c.d / tot) * 1000) / 10, count: tot };
    }).filter(Boolean).sort((a, b) => b.average - a.average);
};
const NPS_RANGE_ZONES = [
    { x1: -100, x2: 0,   fill: '#E74C3C' },
    { x1: 0,    x2: 20,  fill: '#E67E22' },
    { x1: 20,   x2: 50,  fill: '#D4AC0D' },
    { x1: 50,   x2: 70,  fill: '#62C190' },
    { x1: 70,   x2: 100, fill: '#1E8449' },
];

const getNpsBarColor = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '#94a3b8';
    if (n > 70)  return '#1E8449';
    if (n >= 50) return '#62C190';
    if (n >= 20) return '#D4AC0D';
    if (n >= 0)  return '#E67E22';
    return '#E74C3C';
};

const NpsBarChart = ({ rows = [], maxItems = 20, selectedName, onSelect }) => {
    if (!rows.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin datos" style={{ padding: '16px 0' }} />;

    const chartData = (maxItems ? rows.slice(0, maxItems) : rows).map((r) => ({
        name:     (r.name || '').length > 30 ? (r.name || '').substring(0, 28) + '\u2026' : (r.name || ''),
        fullName: r.name,
        value:    r.average != null ? Math.round(r.average * 10) / 10 : 0,
        count:    r.count,
    }));

    const vals      = chartData.map((r) => r.value);
    const domainMin = Math.min(-10, ...vals);
    const domainMax = Math.max(100, ...vals);

    const renderNpsLabel = ({ x, y, width, height, value }) => {
        const isNeg = value < 0;
        const cx = isNeg ? (x || 0) - 4 : (x || 0) + (width || 0) + 4;
        const cy = (y || 0) + (height || 0) / 2;
        return (
            <text x={cx} y={cy} fill="#334155" fontSize={11} fontWeight={800}
                  dominantBaseline="middle" textAnchor={isNeg ? 'end' : 'start'}>
                {value}
            </text>
        );
    };

    const CustomTooltipNPS = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const v   = payload[0]?.value;
        const cnt = payload[0]?.payload?.count;
        return (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', padding: '10px 14px',
                          borderRadius: 8, boxShadow: '0 4px 16px rgba(48,56,142,0.12)' }}>
                <div style={{ fontWeight: 700, fontSize: 12, color: '#334155', marginBottom: 6 }}>
                    {payload[0]?.payload?.fullName}
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: getNpsBarColor(v), lineHeight: 1 }}>
                    {v}
                </div>
                {cnt != null && (
                    <div style={{ fontSize: 11, color: '#617993', marginTop: 3 }}>
                        {cnt} evaluado{cnt !== 1 ? 's' : ''}
                    </div>
                )}
            </div>
        );
    };

    const barSize = 14;
    const height  = Math.max(140, chartData.length * (barSize + 20) + 50);

    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart data={chartData} layout="vertical"
                      margin={{ top: 6, right: 52, bottom: 4, left: 8 }}
                      onClick={onSelect ? (data) => {
                          const name = data?.activePayload?.[0]?.payload?.fullName;
                          if (name) onSelect(name);
                      } : undefined}>
                {NPS_RANGE_ZONES.map((z) => (
                    <ReferenceArea key={z.x1} x1={z.x1} x2={z.x2} fill={z.fill} fillOpacity={0.05} />
                ))}
                <CartesianGrid stroke="#e8eef5" horizontal={false} vertical={true} strokeDasharray="2 4" />
                <XAxis
                    type="number"
                    domain={[domainMin, domainMax]}
                    ticks={[-50, 0, 20, 50, 70, 100].filter((t) => t >= domainMin && t <= domainMax)}
                    tick={{ fontSize: 9, fill: '#8c8c8c' }}
                    axisLine={false}
                    tickLine={false}
                    orientation="top"
                />
                <YAxis
                    type="category"
                    dataKey="name"
                    width={170}
                    interval={0}
                    tick={{ fontSize: 11, fill: '#334155' }}
                    axisLine={false}
                    tickLine={false}
                />
                <Tooltip content={<CustomTooltipNPS />} />
                <ReferenceLine x={0} stroke="#94a3b8" strokeWidth={1} />
                <ReferenceLine x={50} stroke="#62C190" strokeWidth={1} strokeDasharray="4 2" />
                <Bar dataKey="value" barSize={barSize} radius={[0, 6, 6, 0]} isAnimationActive={false}
                     style={onSelect ? { cursor: 'pointer' } : undefined}>
                    {chartData.map((entry, index) => (
                        <Cell
                            key={`cell-${index}`}
                            fill={getNpsBarColor(entry.value)}
                            opacity={selectedName && entry.fullName !== selectedName ? 0.35 : 1}
                        />
                    ))}
                    <LabelList dataKey="value" content={renderNpsLabel} />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

// ─── CoveragePanel ────────────────────────────────────────────────────────────
// areaScore: when provided, displays the area's instrument score instead of coverage %
const CoveragePanel = ({ coverage, total, evaluated, breachCount, totalDims, instrument, areaScore }) => {
    const threshold  = instrument === 'ipra' ? 75 : 65;
    const isAreaMode = areaScore != null;

    // In area mode: use the instrument score (0-100) for color/bar
    const score  = isAreaMode ? areaScore : coverage;
    const pct    = score != null ? Math.round(score * 10) / 10 : null;

    // Colors: in area mode use instrument thresholds; in coverage mode use 80/60
    const color = pct == null ? '#94a3b8'
        : isAreaMode
            ? (instrument === 'ipra'
                ? (pct >= 85 ? '#62C190' : pct >= 75 ? '#D4AC0D' : pct >= 65 ? '#E67E22' : '#E74C3C')
                : (pct >= 80 ? '#62C190' : pct >= 65 ? '#D4AC0D' : pct >= 50 ? '#E67E22' : '#E74C3C'))
            : (pct >= 80 ? '#62C190' : pct >= 60 ? '#D4AC0D' : '#E74C3C');

    const statusText = pct == null ? 'Sin datos'
        : isAreaMode
            ? (instrument === 'ipra'
                ? (pct >= 85 ? 'Óptimo' : pct >= 75 ? 'Aceptable' : pct >= 65 ? 'Riesgo' : 'Crítico')
                : (pct >= 80 ? 'Óptimo' : pct >= 65 ? 'Aceptable' : pct >= 50 ? 'Riesgo' : 'Crítico'))
            : (pct >= 80 ? 'Concluyente' : pct >= 60 ? 'Indicativo' : 'No concluyente');

    return (
        <div className="db-coverage-panel">
            <div className="db-coverage-hero">
                <div className="db-coverage-eyebrow">{isAreaMode ? `Score ${instrument.toUpperCase()}` : 'Cobertura'}</div>
                <div className="db-coverage-pct" style={{ color }}>{pct != null ? pct : '–'}</div>
                <div className="db-coverage-status-badge" style={{ background: `${color}1a`, color }}>
                    {statusText}
                </div>
            </div>
            <div className="db-coverage-bar-wrap">
                <div className="db-coverage-bar-track">
                    <div className="db-coverage-bar-fill" style={{ width: `${Math.min(pct || 0, 100)}%`, background: color }} />
                </div>
            </div>
            <div className="db-coverage-stats">
                {!isAreaMode && (
                    <>
                        <div className="db-coverage-stat">
                            <span className="db-coverage-stat-val">{total ?? '–'}</span>
                            <span className="db-coverage-stat-lbl">Universo</span>
                        </div>
                        <div className="db-coverage-stat">
                            <span className="db-coverage-stat-val" style={{ color }}>{evaluated ?? '–'}</span>
                            <span className="db-coverage-stat-lbl">Evaluados</span>
                        </div>
                    </>
                )}
                <div className="db-coverage-stat">
                    <span className="db-coverage-stat-val"
                          style={{ color: breachCount > 0 ? '#E74C3C' : '#62C190' }}>
                        {breachCount} / {totalDims}
                    </span>
                    <span className="db-coverage-stat-lbl">Bajo umbral {threshold}</span>
                </div>
            </div>
        </div>
    );
};

// ─── IVC level badge ──────────────────────────────────────────────────────────
 const IVC_LABEL  = { 0: 'Sin co-falla', 1: 'Moderado', 2: 'Alto', 3: 'Crítico' };
const IVC_COLOR  = { 0: 'green', 1: 'gold', 2: 'orange', 3: 'red' };
const IVC_HEX    = { 0: '#62C190', 1: '#D4AC0D', 2: '#E67E22', 3: '#E74C3C' };
const IVC_ACTION = { 0: 'Monitoreo preventivo', 1: 'Plan de mejora en 30 días', 2: 'Intervención esta semana', 3: 'Intervención urgente' };

// ─── Score color helpers ──────────────────────────────────────────────────────
 const ipraScoreColor = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '#94a3b8';
    if (n >= 85) return '#62C190';
    if (n >= 75) return '#D4AC0D';
    if (n >= 65) return '#E67E22';
    return '#E74C3C';
};
const iashScoreColor = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '#94a3b8';
    if (n >= 80) return '#62C190';
    if (n >= 65) return '#D4AC0D';
    if (n >= 50) return '#E67E22';
    return '#E74C3C';
};

// ─── AreaFocusCard ────────────────────────────────────────────────────────────
const AreaFocusCard = ({ area, icp, ipraAverage, iashAverage, ivcLevel, headcount }) => {
    const hex    = IVC_HEX[ivcLevel]    ?? '#94a3b8';
    const label  = IVC_LABEL[ivcLevel]  ?? 'Sin dato';
    const action = IVC_ACTION[ivcLevel] ?? '';
    return (
        <div className="db-area-focus-card" style={{ borderLeftColor: hex }}>
            <div className="db-area-focus-header">
                <span className="db-area-focus-name">{area}</span>
                <span className="db-area-focus-badge" style={{ background: hex }}>
                    {label.toUpperCase()}
                </span>
            </div>
            <div className="db-area-focus-metrics">
                <div className="db-area-focus-metric">
                    <span className="db-area-focus-metric-label">ICP</span>
                    <span className="db-area-focus-metric-value" style={{ color: hex }}>
                        {formatRounded(icp)}
                    </span>
                </div>
                <div className="db-area-focus-metric">
                    <span className="db-area-focus-metric-label">IPRA</span>
                    <span className="db-area-focus-metric-value" style={{ color: ipraScoreColor(ipraAverage) }}>
                        {formatRounded(ipraAverage)}
                    </span>
                </div>
                <div className="db-area-focus-metric">
                    <span className="db-area-focus-metric-label">IASH</span>
                    <span className="db-area-focus-metric-value" style={{ color: iashScoreColor(iashAverage) }}>
                        {formatRounded(iashAverage)}
                    </span>
                </div>
                {headcount != null && (
                    <div className="db-area-focus-metric">
                        <span className="db-area-focus-metric-label">N°</span>
                        <span className="db-area-focus-metric-value">{headcount}</span>
                    </div>
                )}
            </div>
            <div className="db-area-focus-action" style={{ color: hex }}>
                → {action}
            </div>
        </div>
    );
};

// ── Safety file uploader (Dragger) ────────────────────────────────────────────
const SafetyUploader = ({ loading, result, onFile }) => {
    const draggerProps = {
        name: 'file',
        multiple: false,
        accept: '.xlsx,.xls',
        showUploadList: false,
        beforeUpload: (file) => {
            onFile([file]);
            return false; // prevent antd auto-upload
        },
    };

    return (
        <div className="managerial-safety-upload-section">
            <div className="managerial-safety-upload-label">
                Cargar / actualizar datos de seguridad (hojas 5_ACCIDENTES y 6_AUSENTISMO)
            </div>
            <Spin spinning={loading} tip="Leyendo archivo…">
                <Upload.Dragger {...draggerProps} style={{ padding: '12px 0' }}>
                    <p className="ant-upload-drag-icon">
                        {loading ? <InboxOutlined style={{ color: '#1890ff' }} /> : <FileExcelOutlined style={{ color: '#52c41a', fontSize: 32 }} />}
                    </p>
                    <p className="ant-upload-text">Arrastra el archivo Excel aquí o haz clic para seleccionarlo</p>
                    <p className="ant-upload-hint">Formatos aceptados: .xlsx, .xls — máx. 20 MB</p>
                </Upload.Dragger>
            </Spin>
            {result && (
                <Alert
                    style={{ marginTop: 8 }}
                    type={result.errors?.length ? 'warning' : 'success'}
                    showIcon
                    message={`Accidentes: ${result.accidents?.created ?? 0} cargados, ${result.accidents?.skipped ?? 0} duplicados  ·  Ausentismos: ${result.absences?.created ?? 0} cargados, ${result.absences?.skipped ?? 0} duplicados`}
                    description={result.errors?.length ? `Errores: ${result.errors.join(' | ')}` : null}
                />
            )}
        </div>
    );
};


const ManagerialCrossDashboard = ({ reportData, orgLogo, orgName, onOpenFullReport }) => {
    const exporterRef    = useRef(null);
    const areaReportRef  = useRef(null);
    const [exportLoading,    setExportLoading]    = useState(false);
    const [selectedArea,     setSelectedArea]     = useState(null);
    const [activeTab,        setActiveTab]        = useState('overview');
    const [areaReportOpen,   setAreaReportOpen]   = useState(false);
    const [areaReportTarget, setAreaReportTarget] = useState(null);
    const [collabFilters,    setCollabFilters]    = useState({});

    // ── Per-persona states (IASH/IPRA Personas tabs) ──────────────────────────
    const [selectedIashDni,   setSelectedIashDni]   = useState(null);
    const [iashPersonaData,   setIashPersonaData]   = useState(null);
    const [iashPersonaLoading, setIashPersonaLoading] = useState(false);
    const [selectedIpraDni,   setSelectedIpraDni]   = useState(null);
    const [ipraPersonaData,   setIpraPersonaData]   = useState(null);
    const [ipraPersonaLoading, setIpraPersonaLoading] = useState(false);

    // ── Safety data (accidents + absences) ────────────────────────────────────
    const [accidents,           setAccidents]           = useState([]);
    const [absences,            setAbsences]            = useState([]);
    const [safetyLoading,       setSafetyLoading]       = useState(false);
    const [safetyUploadLoading, setSafetyUploadLoading] = useState(false);
    const [uploadResult,        setUploadResult]        = useState(null);
    const [previewData,         setPreviewData]         = useState(null);
    const [previewOpen,         setPreviewOpen]         = useState(false);
    const [pendingFile,         setPendingFile]         = useState(null);

    const payload     = useMemo(() => reportData?.payload || {}, [reportData]);
    const evaluations = useMemo(() => payload?.evaluations || {}, [payload]);
    const kpis        = useMemo(() => payload?.kpis || {}, [payload]);
    const ipraTotals  = useMemo(() => payload?.totals?.ipra || {}, [payload]);
    const iashTotals  = useMemo(() => payload?.totals?.iash || {}, [payload]);

    const scoresByDni = useMemo(() => payload?.employee_scores || {}, [payload]);

    // ── All dimension rows (global) ────────────────────────────────────────────
    const ipraRows = useMemo(() => {
        return [...toDimensionRows(ipraTotals?.average_by_dimension)].sort((a, b) => (a.average ?? 999) - (b.average ?? 999));
    }, [ipraTotals?.average_by_dimension]);

    const iashRows = useMemo(() => {
        return [...toDimensionRows(iashTotals?.average_by_dimension)].sort((a, b) => (a.average ?? 999) - (b.average ?? 999));
    }, [iashTotals?.average_by_dimension]);

    // ── Area options ───────────────────────────────────────────────────────────
    const areaOptions = useMemo(() => {
        const names = new Set([
            ...Object.keys(payload?.ipra_by_area_and_dimension || {}),
            ...Object.keys(payload?.iash_by_area_and_dimension || {}),
        ]);
        return Array.from(names).sort().map((n) => ({ value: n, label: n }));
    }, [payload?.ipra_by_area_and_dimension, payload?.iash_by_area_and_dimension]);

    const accidentsFiltered = useMemo(() => {
        if (!selectedArea) return accidents;
        return accidents.filter((r) => r.employee_management_area === selectedArea);
    }, [accidents, selectedArea]);

    const absencesFiltered = useMemo(() => {
        if (!selectedArea) return absences;
        return absences.filter((r) => r.employee_management_area === selectedArea);
    }, [absences, selectedArea]);

    const collaboratorsFiltered = useMemo(() => {
        const list = payload?.employees_list || [];
        if (!selectedArea) return list;
        return list.filter((e) => e.management_area === selectedArea);
    }, [payload?.employees_list, selectedArea]);

    const getColumnSearchProps = (dataIndex, title) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    placeholder={`Buscar ${title}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => confirm()}
                    style={{ display: 'block', marginBottom: 8 }}
                />
                <Space>
                    <Button type="primary" icon={<SearchOutlined />} onClick={() => confirm()} size="small">Buscar</Button>
                    <Button onClick={() => { clearFilters?.(); confirm(); }} size="small">Limpiar</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
        onFilter: (value, record) =>
            String(record[dataIndex] ?? '').toLowerCase().includes(String(value).toLowerCase()),
    });

    const empGerenciaOptions = useMemo(() =>
        [...new Set((payload?.employees_list || []).map((e) => e.management_area).filter(Boolean))].sort(),
        [payload?.employees_list]
    );

    const empAreaOptions = useMemo(() =>
        [...new Set((payload?.employees_list || []).map((e) => e.area).filter(Boolean))].sort(),
        [payload?.employees_list]
    );

    const empSedeOptions = useMemo(() =>
        [...new Set((payload?.employees_list || []).map((e) => e.sede).filter(Boolean))].sort(),
        [payload?.employees_list]
    );

    // ── Filtered dimension rows (area mode) ────────────────────────────────────
    const filteredIpraRows = useMemo(() => {
        if (!selectedArea) return ipraRows;
        const areaScores = payload?.ipra_by_area_and_dimension?.[selectedArea] || {};
        const globalDimMap = ipraTotals?.average_by_dimension || {};
        return Object.entries(globalDimMap)
            .map(([dimKey, dimInfo]) => ({
                dimension: dimInfo?.title || dimKey,
                average: areaScores[dimKey] ?? null,
                weight: dimInfo?.weight,
                weight_percent: dimInfo?.weight_percent,
            }))
            .filter((r) => r.average != null)
            .sort((a, b) => (a.average ?? 999) - (b.average ?? 999));
    }, [selectedArea, ipraRows, payload?.ipra_by_area_and_dimension, ipraTotals?.average_by_dimension]);

    const filteredIashRows = useMemo(() => {
        if (!selectedArea) return iashRows;
        const areaScores = payload?.iash_by_area_and_dimension?.[selectedArea] || {};
        const globalDimMap = iashTotals?.average_by_dimension || {};
        return Object.entries(globalDimMap)
            .map(([dimKey, dimInfo]) => ({
                dimension: dimInfo?.title || dimKey,
                average: areaScores[dimKey] ?? null,
                weight: dimInfo?.weight,
                weight_percent: dimInfo?.weight_percent,
            }))
            .filter((r) => r.average != null)
            .sort((a, b) => (a.average ?? 999) - (b.average ?? 999));
    }, [selectedArea, iashRows, payload?.iash_by_area_and_dimension, iashTotals?.average_by_dimension]);

    // ── Coverage (global only — per-area coverage not available in payload) ────
    const ipraCoverage = useMemo(() => getCoveragePercent(ipraTotals), [ipraTotals]);
    const iashCoverage = useMemo(() => getCoveragePercent(iashTotals), [iashTotals]);

    // ── Area-level score (for coverage panel in filtered mode) ─────────────────
    const ipraAreaScore = useMemo(() => {
        if (!selectedArea) return null;
        const entry = (ipraTotals?.average_by_management_area || []).find((r) => r.name === selectedArea);
        return entry?.average ?? null;
    }, [selectedArea, ipraTotals?.average_by_management_area]);

    const iashAreaScore = useMemo(() => {
        if (!selectedArea) return null;
        const entry = (iashTotals?.average_by_management_area || []).find((r) => r.name === selectedArea);
        return entry?.average ?? null;
    }, [selectedArea, iashTotals?.average_by_management_area]);

    // ── Breakdown rows para gráficos estilo Power BI ───────────────────────────
    const iashMgmtAreaRows = useMemo(() =>
        [...(iashTotals?.average_by_management_area || [])].sort((a, b) => b.average - a.average),
    [iashTotals?.average_by_management_area]);

    const ipraMgmtAreaRows = useMemo(() =>
        [...(ipraTotals?.average_by_management_area || [])].sort((a, b) => b.average - a.average),
    [ipraTotals?.average_by_management_area]);

    const iashOpAreaRows = useMemo(() => {
        const emps = payload?.employees_list || [];
        const src  = selectedArea ? emps.filter((e) => e.management_area === selectedArea) : emps;
        const map  = {};
        for (const e of src) {
            if (!e.area || e.iash_score == null) continue;
            (map[e.area] = map[e.area] || []).push(e.iash_score);
        }
        return Object.entries(map)
            .map(([name, vals]) => ({ name, average: Math.round(vals.reduce((s, v) => s + v, 0) / vals.length * 10) / 10, count: vals.length }))
            .sort((a, b) => b.average - a.average);
    }, [payload?.employees_list, selectedArea]);

    const ipraOpAreaRows = useMemo(() => {
        const emps = payload?.employees_list || [];
        const src  = selectedArea ? emps.filter((e) => e.management_area === selectedArea) : emps;
        const map  = {};
        for (const e of src) {
            if (!e.area || e.ipra_score == null) continue;
            (map[e.area] = map[e.area] || []).push(e.ipra_score);
        }
        return Object.entries(map)
            .map(([name, vals]) => ({ name, average: Math.round(vals.reduce((s, v) => s + v, 0) / vals.length * 10) / 10, count: vals.length }))
            .sort((a, b) => b.average - a.average);
    }, [payload?.employees_list, selectedArea]);

    const iashJobTitleRows = useMemo(() => {
        const emps = payload?.employees_list || [];
        if (!selectedArea && emps.every((e) => !e.job_title)) {
            return [...(iashTotals?.average_by_job_title || [])].sort((a, b) => b.average - a.average);
        }
        const src = selectedArea ? emps.filter((e) => e.management_area === selectedArea) : emps;
        const map = {};
        for (const e of src) {
            if (!e.job_title || e.iash_score == null) continue;
            (map[e.job_title] = map[e.job_title] || []).push(e.iash_score);
        }
        const rows = Object.entries(map)
            .map(([name, vals]) => ({ name, average: Math.round(vals.reduce((s, v) => s + v, 0) / vals.length * 10) / 10, count: vals.length }))
            .sort((a, b) => b.average - a.average);
        return rows.length > 0 ? rows : [...(iashTotals?.average_by_job_title || [])].sort((a, b) => b.average - a.average);
    }, [payload?.employees_list, selectedArea, iashTotals?.average_by_job_title]);

    const ipraJobTitleRows = useMemo(() => {
        const emps = payload?.employees_list || [];
        if (!selectedArea && emps.every((e) => !e.job_title)) {
            return [...(ipraTotals?.average_by_job_title || [])].sort((a, b) => b.average - a.average);
        }
        const src = selectedArea ? emps.filter((e) => e.management_area === selectedArea) : emps;
        const map = {};
        for (const e of src) {
            if (!e.job_title || e.ipra_score == null) continue;
            (map[e.job_title] = map[e.job_title] || []).push(e.ipra_score);
        }
        const rows = Object.entries(map)
            .map(([name, vals]) => ({ name, average: Math.round(vals.reduce((s, v) => s + v, 0) / vals.length * 10) / 10, count: vals.length }))
            .sort((a, b) => b.average - a.average);
        return rows.length > 0 ? rows : [...(ipraTotals?.average_by_job_title || [])].sort((a, b) => b.average - a.average);
    }, [payload?.employees_list, selectedArea, ipraTotals?.average_by_job_title]);

    const iashJobCategoryRows = useMemo(() => {
        const emps = payload?.employees_list || [];
        if (!selectedArea && emps.every((e) => !e.job_category)) {
            return [...(iashTotals?.average_by_job_category || [])].sort((a, b) => b.average - a.average);
        }
        const src = selectedArea ? emps.filter((e) => e.management_area === selectedArea) : emps;
        const map = {};
        for (const e of src) {
            if (!e.job_category || e.iash_score == null) continue;
            (map[e.job_category] = map[e.job_category] || []).push(e.iash_score);
        }
        const rows = Object.entries(map)
            .map(([name, vals]) => ({ name, average: Math.round(vals.reduce((s, v) => s + v, 0) / vals.length * 10) / 10, count: vals.length }))
            .sort((a, b) => b.average - a.average);
        return rows.length > 0 ? rows : [...(iashTotals?.average_by_job_category || [])].sort((a, b) => b.average - a.average);
    }, [payload?.employees_list, selectedArea, iashTotals?.average_by_job_category]);

    const ipraJobCategoryRows = useMemo(() => {
        const emps = payload?.employees_list || [];
        if (!selectedArea && emps.every((e) => !e.job_category)) {
            return [...(ipraTotals?.average_by_job_category || [])].sort((a, b) => b.average - a.average);
        }
        const src = selectedArea ? emps.filter((e) => e.management_area === selectedArea) : emps;
        const map = {};
        for (const e of src) {
            if (!e.job_category || e.ipra_score == null) continue;
            (map[e.job_category] = map[e.job_category] || []).push(e.ipra_score);
        }
        const rows = Object.entries(map)
            .map(([name, vals]) => ({ name, average: Math.round(vals.reduce((s, v) => s + v, 0) / vals.length * 10) / 10, count: vals.length }))
            .sort((a, b) => b.average - a.average);
        return rows.length > 0 ? rows : [...(ipraTotals?.average_by_job_category || [])].sort((a, b) => b.average - a.average);
    }, [payload?.employees_list, selectedArea, ipraTotals?.average_by_job_category]);

    const iashEvaluatedCount = useMemo(() => {
        if (selectedArea) {
            return (payload?.employees_list || []).filter((e) => e.management_area === selectedArea && e.iash_score != null).length;
        }
        return payload?.total_employee_count || iashTotals?.relationships_finished || 0;
    }, [payload?.employees_list, payload?.total_employee_count, iashTotals?.relationships_finished, selectedArea]);

    const ipraEvaluatedCount = useMemo(() => {
        if (selectedArea) {
            return (payload?.employees_list || []).filter((e) => e.management_area === selectedArea && e.ipra_score != null).length;
        }
        return payload?.total_supervisor_count || ipraTotals?.relationships_finished || 0;
    }, [payload?.employees_list, payload?.total_supervisor_count, ipraTotals?.relationships_finished, selectedArea]);

    // ── IASH item-level rows (Personas tab) ────────────────────────────────────
    const iashItemRows = useMemo(() => {
        const rows = [];
        const srcDimMap = iashPersonaData?.average_by_dimension || null;
        const dimDict   = srcDimMap || iashTotals?.average_by_dimension || {};
        // Direct dimKey lookup — iash_by_area_and_dimension is keyed by the same dimKey as average_by_dimension
        const areaScores = (!srcDimMap && selectedArea)
            ? (payload?.iash_by_area_and_dimension?.[selectedArea] || {})
            : {};
        const sortedDims = Object.entries(dimDict).sort(([, a], [, b]) => {
            const aNum = parseInt((a?.title || '').match(/^D(\d+)/)?.[1] || '99', 10);
            const bNum = parseInt((b?.title || '').match(/^D(\d+)/)?.[1] || '99', 10);
            return aNum - bNum;
        });
        for (const [dimKey, dimInfo] of sortedDims) {
            const dimAvg = srcDimMap
                ? dimInfo?.average
                : (selectedArea ? (areaScores[dimKey] ?? dimInfo?.average) : dimInfo?.average);
            rows.push({ id: `dim-${dimKey}`, label: dimInfo?.title || dimKey, average: dimAvg, isGroup: true });
            for (const sub of (dimInfo?.subdimensions || [])) {
                rows.push({ id: `sub-${sub.id}`, label: sub.question || `Ítem ${sub.id}`, average: sub.average, isGroup: false });
            }
        }
        return rows;
    }, [iashTotals?.average_by_dimension, payload?.iash_by_area_and_dimension, selectedArea, iashPersonaData]);

    // ── IPRA item-level rows (Personas tab) ────────────────────────────────────
    const ipraItemRows = useMemo(() => {
        const rows = [];
        const srcDimMap = ipraPersonaData?.average_by_dimension || null;
        const dimDict   = srcDimMap || ipraTotals?.average_by_dimension || {};
        const areaScores = (!srcDimMap && selectedArea)
            ? (payload?.ipra_by_area_and_dimension?.[selectedArea] || {})
            : {};
        const sortedDims = Object.entries(dimDict).sort(([, a], [, b]) => {
            const aNum = parseInt((a?.title || '').match(/^D(\d+)/)?.[1] || '99', 10);
            const bNum = parseInt((b?.title || '').match(/^D(\d+)/)?.[1] || '99', 10);
            return aNum - bNum;
        });
        for (const [dimKey, dimInfo] of sortedDims) {
            const dimAvg = srcDimMap
                ? dimInfo?.average
                : (selectedArea ? (areaScores[dimKey] ?? dimInfo?.average) : dimInfo?.average);
            rows.push({ id: `dim-${dimKey}`, label: dimInfo?.title || dimKey, average: dimAvg, isGroup: true });
            for (const sub of (dimInfo?.subdimensions || [])) {
                rows.push({ id: `sub-${sub.id}`, label: sub.question || `Ítem ${sub.id}`, average: sub.average, isGroup: false });
            }
        }
        return rows;
    }, [ipraTotals?.average_by_dimension, payload?.ipra_by_area_and_dimension, selectedArea, ipraPersonaData]);

    // ── NPS data ───────────────────────────────────────────────────────────────
    const npsData = useMemo(() => payload?.nps_data || null, [payload?.nps_data]);

    const npsByGerencia = useMemo(() => {
        return (npsData?.by_area || [])
            .map((r) => ({ name: r.area, average: r.nps, count: r.total }))
            .sort((a, b) => b.average - a.average);
    }, [npsData?.by_area]);

    const npsByOpArea = useMemo(() => {
        const emps = payload?.nps_employees || [];
        if (selectedArea) {
            const filtered = emps.filter((e) => e.management_area === selectedArea);
            return _npsFromEmps(filtered, 'area');
        }
        return (npsData?.by_op_area || []).map((r) => ({ name: r.name, average: r.nps, count: r.total }));
    }, [npsData?.by_op_area, payload?.nps_employees, selectedArea]);

    const npsByJobTitle = useMemo(() => {
        const emps = payload?.nps_employees || [];
        if (selectedArea) {
            const filtered = emps.filter((e) => e.management_area === selectedArea);
            return _npsFromEmps(filtered, 'job_title');
        }
        return (npsData?.by_job_title || []).map((r) => ({ name: r.name, average: r.nps, count: r.total }));
    }, [npsData?.by_job_title, payload?.nps_employees, selectedArea]);

    const npsByJobCategory = useMemo(() => {
        const emps = payload?.nps_employees || [];
        if (selectedArea) {
            const filtered = emps.filter((e) => e.management_area === selectedArea);
            return _npsFromEmps(filtered, 'job_category');
        }
        return (npsData?.by_job_category || []).map((r) => ({ name: r.name, average: r.nps, count: r.total }));
    }, [npsData?.by_job_category, payload?.nps_employees, selectedArea]);

    const npsEmployees = useMemo(() => {
        const emps = payload?.nps_employees || [];
        if (selectedArea) return emps.filter((e) => e.management_area === selectedArea);
        return emps;
    }, [payload?.nps_employees, selectedArea]);

    // ── DNI selector options for Personas tabs ────────────────────────────────
    const iashDniOptions = useMemo(() => {
        const list = (payload?.employees_list || [])
            .filter((e) => e.iash_score != null && (!selectedArea || e.management_area === selectedArea));
        return list
            .map((e) => ({ value: String(e.dni), label: `${e.first_name || ''} ${e.last_name || ''} — ${e.dni}`.trim() }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [payload?.employees_list, selectedArea]);

    const ipraDniOptions = useMemo(() => {
        const list = (payload?.employees_list || [])
            .filter((e) => e.ipra_score != null && (!selectedArea || e.management_area === selectedArea));
        return list
            .map((e) => ({ value: String(e.dni), label: `${e.first_name || ''} ${e.last_name || ''} — ${e.dni}`.trim() }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [payload?.employees_list, selectedArea]);

    // ── Per-area Paso M data ───────────────────────────────────────────────────
    const areaIrhData     = useMemo(() => selectedArea ? (payload.irh_by_area?.[selectedArea]          ?? null) : null, [selectedArea, payload]);
    const areaSupervisors = useMemo(() => selectedArea ? (payload.supervisors_by_area?.[selectedArea]   ?? [])   : [], [selectedArea, payload]);
    const areaCofailure   = useMemo(() => selectedArea ? (payload.cofailure_by_area?.[selectedArea]     ?? null) : null, [selectedArea, payload]);
    const areaProfiles    = useMemo(() => selectedArea ? (payload.iash_profiles_by_area?.[selectedArea] ?? null) : null, [selectedArea, payload]);
    const areaGrupo       = useMemo(() => selectedArea ? (payload.grupo_by_area?.[selectedArea]         ?? null) : null, [selectedArea, payload]);

    const areaRows = useMemo(() => {
        const headcountMap = {};
        (payload?.headcount_by_management_area || []).forEach((row) => {
            if (row?.name) headcountMap[row.name] = row.headcount;
        });

        const ipraAreaMap = {};
        (ipraTotals?.average_by_management_area || []).forEach((row) => {
            if (row?.name) ipraAreaMap[row.name] = row.average;
        });

        const iashAreaMap = {};
        (iashTotals?.average_by_management_area || []).forEach((row) => {
            if (row?.name) iashAreaMap[row.name] = row.average;
        });

        const areaNames = new Set([
            ...Object.keys(payload?.icp_by_area || {}),
            ...Object.keys(payload?.ivc_by_area || {}),
            ...Object.keys(headcountMap),
            ...Object.keys(ipraAreaMap),
            ...Object.keys(iashAreaMap),
        ]);

        return Array.from(areaNames).map((areaName) => {
            const ivcData = payload?.ivc_by_area?.[areaName] || {};
            const ipraAverage = ipraAreaMap[areaName];
            const iashAverage = iashAreaMap[areaName];
            const fallbackLevel = computeIvcLevel(ipraAverage, iashAverage);
            const ivcLevel = Number.isFinite(Number(ivcData?.nivel)) ? Number(ivcData.nivel) : fallbackLevel;

            return {
                key: areaName,
                area: areaName,
                headcount: headcountMap[areaName] ?? null,
                icp: payload?.icp_by_area?.[areaName]?.icp ?? null,
                ipraAverage: ipraAverage ?? null,
                iashAverage: iashAverage ?? null,
                ivcLevel,
            };
        }).sort((a, b) => {
            if (b.ivcLevel !== a.ivcLevel) return b.ivcLevel - a.ivcLevel;
            return (a.icp ?? 999) - (b.icp ?? 999);
        });
    }, [payload, ipraTotals?.average_by_management_area, iashTotals?.average_by_management_area]);

    const topCriticalAreas = useMemo(() => areaRows.filter((row) => row.ivcLevel >= 1).slice(0, 5), [areaRows]);

    const selectedAreaRow = useMemo(() => {
        if (!selectedArea) return null;
        return areaRows.find((r) => r.area === selectedArea) || null;
    }, [selectedArea, areaRows]);

    // KPIs to display — area-specific when filtered, global otherwise
    const displayKpis = useMemo(() => {
        if (!selectedArea || !selectedAreaRow) return kpis;
        const ipra = selectedAreaRow.ipraAverage;
        const iash = selectedAreaRow.iashAverage;
        const cross = (ipra != null && iash != null) ? Math.round((ipra + iash) / 2 * 10) / 10 : null;
        return { cross_index: cross, ipra_average: ipra, iash_average: iash, ipra_nps: null };
    }, [selectedArea, selectedAreaRow, kpis]);

    const executiveRead = useMemo(() => {
        const fragments = [];

        if (selectedArea) {
            if (displayKpis?.cross_index != null) {
                fragments.push(`ICP del área ${selectedArea}: ${formatRounded(displayKpis.cross_index)} puntos.`);
            }
            if (filteredIpraRows[0]?.dimension) {
                fragments.push(`Brecha IPRA en el área: ${filteredIpraRows[0].dimension} (${formatRounded(filteredIpraRows[0].average)}).`);
            }
            if (filteredIashRows[0]?.dimension) {
                fragments.push(`Brecha IASH en el área: ${filteredIashRows[0].dimension} (${formatRounded(filteredIashRows[0].average)}).`);
            }
            return fragments.join(' ');
        }

        if (kpis?.cross_index != null) {
            fragments.push(`ICP consolidado en ${formatRounded(kpis.cross_index)} puntos.`);
        }
        if (topCriticalAreas[0]?.area) {
            fragments.push(`El mayor foco inmediato está en ${topCriticalAreas[0].area}.`);
        }
        if (ipraRows[0]?.dimension) {
            fragments.push(`Brecha IPRA dominante: ${ipraRows[0].dimension} (${formatRounded(ipraRows[0].average)}).`);
        }
        if (iashRows[0]?.dimension) {
            fragments.push(`Brecha IASH dominante: ${iashRows[0].dimension} (${formatRounded(iashRows[0].average)}).`);
        }

        return fragments.join(' ');
    }, [iashRows, ipraRows, kpis, topCriticalAreas, selectedArea, displayKpis, filteredIpraRows, filteredIashRows]);

    const areaColumns = [
        {
            title: 'Área',
            dataIndex: 'area',
            key: 'area',
        },
        {
            title: 'ICP',
            dataIndex: 'icp',
            key: 'icp',
            width: 76,
            align: 'center',
            render: (value) => <span style={{ fontWeight: 700 }}>{formatRounded(value)}</span>,
        },
        {
            title: 'IPRA',
            dataIndex: 'ipraAverage',
            key: 'ipraAverage',
            width: 76,
            align: 'center',
            render: (value) => (
                <span style={{ fontWeight: 700, color: ipraScoreColor(value) }}>{formatRounded(value)}</span>
            ),
        },
        {
            title: 'IASH',
            dataIndex: 'iashAverage',
            key: 'iashAverage',
            width: 76,
            align: 'center',
            render: (value) => (
                <span style={{ fontWeight: 700, color: iashScoreColor(value) }}>{formatRounded(value)}</span>
            ),
        },
        {
            title: 'IVC',
            dataIndex: 'ivcLevel',
            key: 'ivcLevel',
            width: 120,
            align: 'center',
            render: (value) => (
                <Tag color={IVC_COLOR[value] || 'default'} style={{ margin: 0 }}>
                    {IVC_LABEL[value] || 'Sin dato'}
                </Tag>
            ),
        },
        {
            title: 'N°',
            dataIndex: 'headcount',
            key: 'headcount',
            width: 64,
            align: 'center',
            render: (value) => value ?? '–',
        },
        {
            title: 'Acción',
            dataIndex: 'ivcLevel',
            key: 'action',
            render: (value) => (
                <span style={{ color: IVC_HEX[value] || '#94a3b8', fontWeight: 600, fontSize: 12 }}>
                    {IVC_ACTION[value] || '–'}
                </span>
            ),
        },
        {
            title: '',
            key: 'informe',
            width: 56,
            align: 'center',
            render: (_, record) => (
                <Button
                    size="small"
                    icon={<SnippetsOutlined />}
                    title={`Informe de Área — ${record.area}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        setAreaReportTarget(record.area);
                        setAreaReportOpen(true);
                    }}
                />
            ),
        },
    ];

    const handleExport = async (action) => {
        if (!exporterRef.current) return;

        setExportLoading(true);
        try {
            if (action === 'download') {
                await exporterRef.current.downloadPdf();
                return;
            }
            await exporterRef.current.openPdf();
        } finally {
            setExportLoading(false);
        }
    };

    const gcrId = reportData?.gcr_id;

    // ── Fetch individual IASH persona scores ──────────────────────────────────
    useEffect(() => {
        if (!selectedIashDni || !gcrId) { setIashPersonaData(null); return; }
        const access = localStorage.getItem('access_token');
        if (!access) return;
        setIashPersonaLoading(true);
        axios.get(`${apiUrl}/informs/get-managerial-cross-employee/${gcrId}/${selectedIashDni}/`,
            { headers: { Authorization: `Bearer ${access}` } })
            .then((res) => setIashPersonaData(res.data?.iash || null))
            .catch(() => setIashPersonaData(null))
            .finally(() => setIashPersonaLoading(false));
    }, [selectedIashDni, gcrId]);

    // ── Fetch individual IPRA persona scores ──────────────────────────────────
    useEffect(() => {
        if (!selectedIpraDni || !gcrId) { setIpraPersonaData(null); return; }
        const access = localStorage.getItem('access_token');
        if (!access) return;
        setIpraPersonaLoading(true);
        axios.get(`${apiUrl}/informs/get-managerial-cross-employee/${gcrId}/${selectedIpraDni}/`,
            { headers: { Authorization: `Bearer ${access}` } })
            .then((res) => setIpraPersonaData(res.data?.ipra || null))
            .catch(() => setIpraPersonaData(null))
            .finally(() => setIpraPersonaLoading(false));
    }, [selectedIpraDni, gcrId]);

    // ── Reset DNIs when area changes ──────────────────────────────────────────
    useEffect(() => {
        setSelectedIashDni(null);
        setIashPersonaData(null);
        setSelectedIpraDni(null);
        setIpraPersonaData(null);
    }, [selectedArea]);

    // ── Reset collaborator column filters when area or tab changes ────────────
    useEffect(() => { setCollabFilters({}); }, [selectedArea]);
    useEffect(() => { if (activeTab !== 'collaborators') setCollabFilters({}); }, [activeTab]);

    // ── Safety data load ───────────────────────────────────────────────────────
    useEffect(() => {
        if (!gcrId) return;
        const access = localStorage.getItem('access_token');
        if (!access) return;

        setSafetyLoading(true);
        const headers = { headers: { Authorization: `Bearer ${access}` } };
        Promise.all([
            axios.get(`${apiUrl}/accidents/get-accidents/${gcrId}/`, headers),
            axios.get(`${apiUrl}/absences/get-absences/${gcrId}/`, headers),
        ])
            .then(([accRes, absRes]) => {
                setAccidents(accRes.data || []);
                setAbsences(absRes.data || []);
            })
            .catch((err) => {
                console.error('Error cargando datos de seguridad', err);
            })
            .finally(() => setSafetyLoading(false));
    }, [gcrId]);

    const handleSafetyFileDrop = (files) => {
        const file = Array.isArray(files) ? files[0] : files;
        if (!file || !gcrId) return;
        const access = localStorage.getItem('access_token');
        if (!access) return;

        setPendingFile(file);
        setSafetyUploadLoading(true);

        const formData = new FormData();
        formData.append('file', file);

        axios
            .post(`${apiUrl}/accidents/preview-safety-data/${gcrId}/`, formData, {
                headers: { Authorization: `Bearer ${access}`, 'Content-Type': 'multipart/form-data' },
            })
            .then((res) => {
                setPreviewData(res.data);
                setPreviewOpen(true);
            })
            .catch(() => {
                message.error('Error al leer el archivo. Verifica que contenga las hojas 5_ACCIDENTES y 6_AUSENTISMO.');
                setPendingFile(null);
            })
            .finally(() => setSafetyUploadLoading(false));
    };

    const confirmSafetyUpload = () => {
        if (!pendingFile || !gcrId) return;
        const access = localStorage.getItem('access_token');
        if (!access) return;

        const formData = new FormData();
        formData.append('file', pendingFile);

        setPreviewOpen(false);
        setSafetyUploadLoading(true);
        setUploadResult(null);

        axios
            .post(`${apiUrl}/accidents/upload-safety-data/${gcrId}/`, formData, {
                headers: { Authorization: `Bearer ${access}`, 'Content-Type': 'multipart/form-data' },
            })
            .then((res) => {
                setUploadResult(res.data);
                message.success('Datos cargados correctamente');
                setPendingFile(null);
                setPreviewData(null);
                const headers = { headers: { Authorization: `Bearer ${access}` } };
                Promise.all([
                    axios.get(`${apiUrl}/accidents/get-accidents/${gcrId}/`, headers),
                    axios.get(`${apiUrl}/absences/get-absences/${gcrId}/`, headers),
                ]).then(([accRes, absRes]) => {
                    setAccidents(accRes.data || []);
                    setAbsences(absRes.data || []);
                });
            })
            .catch(() => message.error('Error al guardar los datos'))
            .finally(() => setSafetyUploadLoading(false));
    };

    if (!reportData) {
        return <Empty description="Selecciona una corrida para cargar el dashboard gerencial" />;
    }

    const tabItems = [
        {
            key: 'overview',
            label: 'Resumen',
            children: (
                <div className="managerial-dashboard-tab-content">
                    {executiveRead && (
                        <Alert type="info" showIcon message="Lectura ejecutiva" description={executiveRead} />
                    )}

                    <div className="managerial-dashboard-kpi-strip">
                        <ManCrossKpiCard
                            value={formatRounded(displayKpis?.cross_index)}
                            label={selectedArea ? 'ICP Área' : 'ICP Planta'}
                            badge={getRiskLabel(displayKpis?.cross_index)}
                            colors={KPI_TONE_STYLES[getStatusTone(displayKpis?.cross_index)] || KPI_TONE_STYLES.neutral}
                        />
                        <ManCrossKpiCard
                            value={formatRounded(displayKpis?.ipra_average)}
                            label="IPRA"
                            badge={getRiskLabel(displayKpis?.ipra_average, 'ipra')}
                            colors={KPI_TONE_STYLES[getStatusTone(displayKpis?.ipra_average, 'ipra')] || KPI_TONE_STYLES.neutral}
                        />
                        <ManCrossKpiCard
                            value={formatRounded(displayKpis?.iash_average)}
                            label="IASH"
                            badge={getRiskLabel(displayKpis?.iash_average, 'iash')}
                            colors={KPI_TONE_STYLES[getStatusTone(displayKpis?.iash_average, 'iash')] || KPI_TONE_STYLES.neutral}
                        />
                        {!selectedArea && (
                            <ManCrossKpiCard
                                value={formatRounded(kpis?.ipra_nps, { signed: true })}
                                label="NPS Seguridad"
                                badge={Number(kpis?.ipra_nps) >= 0 ? 'Promotores >' : 'Detractores >'}
                                colors={KPI_TONE_STYLES[getStatusTone(kpis?.ipra_nps, 'nps')] || KPI_TONE_STYLES.neutral}
                            />
                        )}
                        {!selectedArea && (
                            <ManCrossKpiCard
                                value={formatPercent(ipraCoverage)}
                                label="Cobertura IPRA"
                                badge={ipraCoverage >= 80 ? 'Concluyente' : ipraCoverage >= 60 ? 'Indicativo' : 'No concluyente'}
                                colors={ipraCoverage >= 80 ? KPI_TONE_STYLES.low : ipraCoverage >= 60 ? KPI_TONE_STYLES.moderate : KPI_TONE_STYLES.high}
                            />
                        )}
                        {selectedArea && selectedAreaRow && (
                            <ManCrossKpiCard
                                value={IVC_LABEL[selectedAreaRow.ivcLevel] || 'Sin dato'}
                                label="IVC Área"
                                badge={IVC_ACTION[selectedAreaRow.ivcLevel] || ''}
                                colors={KPI_TONE_STYLES[selectedAreaRow.ivcLevel >= 3 ? 'critical' : selectedAreaRow.ivcLevel >= 2 ? 'high' : selectedAreaRow.ivcLevel >= 1 ? 'moderate' : 'low'] || KPI_TONE_STYLES.neutral}
                            />
                        )}
                    </div>

                    <Row gutter={[16, 16]} align="top">
                        <Col xs={24} xl={14}>
                            <Card title={selectedArea ? `Trazabilidad · ${selectedArea}` : 'Trazabilidad del cruce'}>
                                <Descriptions column={1} size="small" bordered>
                                    <Descriptions.Item label="Organización">{orgName || reportData?.org_name || '-'}</Descriptions.Item>
                                    {selectedArea && (
                                        <Descriptions.Item label="Área filtrada">
                                            <Tag color="blue">{selectedArea}</Tag>
                                        </Descriptions.Item>
                                    )}
                                    <Descriptions.Item label="Evaluación IPRA">{evaluations?.ipra?.title || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Evaluación IASH">{evaluations?.iash?.title || '-'}</Descriptions.Item>
                                    {!selectedArea && (
                                        <>
                                            <Descriptions.Item label="Cobertura IPRA">{formatPercent(ipraCoverage)}</Descriptions.Item>
                                            <Descriptions.Item label="Cobertura IASH">{formatPercent(iashCoverage)}</Descriptions.Item>
                                        </>
                                    )}
                                    <Descriptions.Item label="Brechas IPRA">{countBelowThreshold(filteredIpraRows, 75)} / {filteredIpraRows.length} dim. bajo umbral</Descriptions.Item>
                                    <Descriptions.Item label="Brechas IASH">{countBelowThreshold(filteredIashRows, 65)} / {filteredIashRows.length} dim. bajo umbral</Descriptions.Item>
                                </Descriptions>
                            </Card>
                        </Col>
                        <Col xs={24} xl={10}>
                            <div className="managerial-dashboard-semaphore-wrap">
                                <div className="managerial-dashboard-semaphore-title">Semáforo IPRA · IASH</div>
                                <div className="managerial-dashboard-semaphore-grid">
                                    <span className="mds-col-head" />
                                    <span className="mds-col-head">IPRA</span>
                                    <span className="mds-col-head">IASH</span>
                                    <span className="mds-col-head">Riesgo</span>
                                    {SUMMARY_SEMAPHORE_ROWS.map((row) => (
                                        <React.Fragment key={row.risk}>
                                            <span className="mds-dot" style={{ background: row.color }} />
                                            <span className="mds-cell">{row.ipra}</span>
                                            <span className="mds-cell">{row.iash}</span>
                                            <span className="mds-cell mds-cell-risk" style={{ color: row.color }}>{row.risk}</span>
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        </Col>
                    </Row>
                    {selectedArea && areaIrhData && (
                        <Row gutter={[16, 16]} style={{ marginTop: 0 }}>
                            <Col xs={24}>
                                <Card
                                    size="small"
                                    title={
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span>IRH del área · {selectedArea}</span>
                                            {areaGrupo && (
                                                <Tag style={{ margin: 0, fontWeight: 700 }} color={GRUPO_DASH_COLOR[areaGrupo] || 'default'}>
                                                    Grupo {areaGrupo}
                                                </Tag>
                                            )}
                                        </span>
                                    }
                                >
                                    <div className="db-area-irh-strip">
                                        {[
                                            { val: areaIrhData.total_q1 ?? 0, lbl: 'Operarios Q1 (riesgo extremo)', color: '#E74C3C' },
                                            { val: areaIrhData.irh_avg != null ? Math.round(areaIrhData.irh_avg * 10) / 10 : '—', lbl: 'IRH promedio del área', color: irhAvgColor(areaIrhData.irh_avg) },
                                            { val: areaIrhData.quadrant_counts?.[4] ?? 0, lbl: 'Operarios Q4 (zona segura)', color: '#62C190' },
                                            {
                                                val: (() => {
                                                    const q1 = areaIrhData.total_q1 ?? 0;
                                                    const total = Object.values(areaIrhData.quadrant_counts || {}).reduce((s, n) => s + n, 0);
                                                    return total > 0 ? `${Math.round(q1 / total * 10)} de 10` : '—';
                                                })(),
                                                lbl: 'De cada 10, en Q1',
                                                color: '#E74C3C',
                                            },
                                        ].map(({ val, lbl, color }) => (
                                            <div key={lbl} className="db-area-irh-stat">
                                                <div className="db-area-irh-val" style={{ color }}>{val}</div>
                                                <div className="db-area-irh-lbl">{lbl}</div>
                                            </div>
                                        ))}
                                    </div>
                                    {areaGrupo && GRUPO_DASH_DESC[areaGrupo] && (
                                        <div className="db-area-grupo-desc">{GRUPO_DASH_DESC[areaGrupo]}</div>
                                    )}
                                </Card>
                            </Col>
                        </Row>
                    )}
                </div>
            ),
        },
        {
            key: 'areas',
            label: 'Riesgo por área',
            children: (
                <div className="managerial-dashboard-tab-content">
                    <Row gutter={[16, 16]} align="top">
                        <Col xs={24} xl={7}>
                            <Card title={selectedArea ? `Foco: ${selectedArea}` : 'Foco inmediato'}>
                                {selectedArea ? (
                                    selectedAreaRow ? (
                                        <div className="db-area-focus-list">
                                            <AreaFocusCard {...selectedAreaRow} />
                                        </div>
                                    ) : (
                                        <div className="db-area-focus-empty">
                                            Sin datos para esta área.
                                        </div>
                                    )
                                ) : (
                                    topCriticalAreas.length === 0 ? (
                                        <div className="db-area-focus-empty">
                                            Sin áreas en co-falla activa en este cruce.
                                        </div>
                                    ) : (
                                        <div className="db-area-focus-list">
                                            {topCriticalAreas.map((item) => (
                                                <AreaFocusCard key={item.area} {...item} />
                                            ))}
                                        </div>
                                    )
                                )}
                            </Card>
                        </Col>
                        <Col xs={24} xl={17}>
                            <Card title="Mapa de áreas">
                                <Table
                                    rowKey="key"
                                    dataSource={areaRows}
                                    columns={areaColumns}
                                    pagination={false}
                                    size="small"
                                    className="managerial-area-table"
                                    rowClassName={(record) =>
                                        `db-area-row-ivc db-area-row-ivc-${record.ivcLevel}${record.area === selectedArea ? ' db-area-row-selected' : ''}`
                                    }
                                    scroll={{ x: 'max-content' }}
                                    onRow={(record) => ({
                                        onClick: () => setSelectedArea(record.area === selectedArea ? null : record.area),
                                        style: { cursor: 'pointer' },
                                    })}
                                />
                            </Card>
                        </Col>
                    </Row>
                </div>
            ),
        },       
        {
            key: 'iash',
            label: 'IASH · Autocuidado',
            children: (
                <div className="managerial-dashboard-tab-content">
                    {/* Toolbar: KPIs + filtro Gerencia */}
                    <div className="db-pbi-toolbar">
                        <div className="db-pbi-kpis">
                            <div className="db-pbi-kpi-block">
                                <div className="db-pbi-kpi-value">{iashEvaluatedCount}</div>
                                <div className="db-pbi-kpi-label">Personas Evaluadas</div>
                            </div>
                            <div className="db-pbi-kpi-block" style={{ color: iashScoreColor(selectedArea ? iashAreaScore : kpis?.iash_average) }}>
                                <div className="db-pbi-kpi-value">{formatRounded(selectedArea ? iashAreaScore : kpis?.iash_average)}</div>
                                <div className="db-pbi-kpi-label">IASH · Autocuidado</div>
                            </div>
                            <div className="db-pbi-kpi-block">
                                <div className="db-pbi-kpi-value" style={{ color: countBelowThreshold(filteredIashRows, 65) > 0 ? '#E74C3C' : '#62C190' }}>
                                    {countBelowThreshold(filteredIashRows, 65)} / {filteredIashRows.length}
                                </div>
                                <div className="db-pbi-kpi-label">Dim. bajo umbral (65)</div>
                            </div>
                        </div>
                        {selectedArea && (
                            <div className="db-pbi-filter">
                                <Button
                                    size="small"
                                    icon={<SnippetsOutlined />}
                                    onClick={() => { setAreaReportTarget(selectedArea); setAreaReportOpen(true); }}
                                >
                                    Informe · {selectedArea}
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Fila 1: por Dimensión (línea) + por Gerencia */}
                    <Row gutter={[16, 16]} align="top">
                        <Col xs={24} xl={12}>
                            <Card title={`Puntaje IASH por Dimensión${selectedArea ? ` · ${selectedArea}` : ''}`}>
                                <DimensionLineChart rows={filteredIashRows} instrument="iash" />
                            </Card>
                        </Col>
                        <Col xs={24} xl={12}>
                            <Card title="IASH · Autocuidado por Gerencia">
                                <BreakdownBarChart
                                    rows={iashMgmtAreaRows}
                                    instrument="iash"
                                    selectedName={selectedArea}
                                    onSelect={(name) => setSelectedArea(name === selectedArea ? null : name)}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Fila 2: por Área + por Cargo */}
                    <Row gutter={[16, 16]} align="top">
                        <Col xs={24} xl={12}>
                            <Card title={`IASH por Área${selectedArea ? ` · ${selectedArea}` : ''}`}>
                                <BreakdownBarChart rows={iashOpAreaRows} instrument="iash" maxItems={15} />
                            </Card>
                        </Col>
                        <Col xs={24} xl={12}>
                            <Card title={`IASH por Cargo${selectedArea ? ` · ${selectedArea}` : ''}`}>
                                <BreakdownBarChart rows={iashJobTitleRows} instrument="iash" maxItems={15} />
                            </Card>
                        </Col>
                    </Row>

                    {/* Fila 3: por Categoría de Cargo + perfiles/co-fallas si hay gerencia */}
                    <Row gutter={[16, 16]} align="top">
                        <Col xs={24} xl={selectedArea && (areaProfiles || areaCofailure) ? 12 : 24}>
                            <Card title={`IASH por Categoría de Cargo${selectedArea ? ` · ${selectedArea}` : ''}`}>
                                <BreakdownBarChart rows={iashJobCategoryRows} instrument="iash" />
                            </Card>
                        </Col>
                        {selectedArea && (areaProfiles || areaCofailure) && (
                            <Col xs={24} xl={12}>
                                {areaProfiles && (areaProfiles.total ?? 0) > 0 && (
                                    <Card
                                        size="small"
                                        title={`Perfiles IASH · ${selectedArea} — ${areaProfiles.total ?? 0} evaluados`}
                                        style={{ marginBottom: areaCofailure ? 16 : 0 }}
                                    >
                                        <div className="db-area-profiles">
                                            {PROFILE_ORDER_DB.filter((n) => (areaProfiles.profiles?.[n] ?? 0) > 0).map((name) => {
                                                const count = areaProfiles.profiles[name];
                                                const pct   = areaProfiles.total > 0 ? Math.round(count / areaProfiles.total * 100) : 0;
                                                const color = PROFILE_COLORS_DB[name] || '#9AA2AE';
                                                return (
                                                    <div key={name} className="db-area-profile-row">
                                                        <span className="db-area-profile-name" style={{ color }}>{name}</span>
                                                        <div className="db-area-profile-track">
                                                            <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4 }} />
                                                        </div>
                                                        <span className="db-area-profile-pct" style={{ color }}>{pct}%</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </Card>
                                )}
                                {areaCofailure && (
                                    <Card size="small" title="Co-fallas críticas del área">
                                        <div className="db-area-cofail-cards">
                                            {CF_CRITICAL.map(({ key, label, bg, mult }) => {
                                                const d = areaCofailure[key];
                                                if (!d) return null;
                                                return (
                                                    <div key={key} className="db-area-cofail-card" style={{ borderLeftColor: bg }}>
                                                        <div className="db-area-cofail-label">{label}</div>
                                                        <div className="db-area-cofail-count" style={{ color: bg }}>
                                                            {d.count ?? 0}
                                                            <span style={{ fontSize: 12, fontWeight: 400, marginLeft: 4 }}>
                                                                ({d.pct ?? 0}%) — {mult} más probable
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </Card>
                                )}
                            </Col>
                        )}
                    </Row>
                </div>
            ),
        },
        {
            key: 'iash-personas',
            label: 'IASH · Personas',
            children: (
                <div className="managerial-dashboard-tab-content">
                    {/* Toolbar */}
                    <div className="db-pbi-toolbar">
                        <div className="db-pbi-kpis">
                            <div className="db-pbi-kpi-block">
                                <div className="db-pbi-kpi-value">{iashEvaluatedCount}</div>
                                <div className="db-pbi-kpi-label">Personas Evaluadas</div>
                            </div>
                            <div className="db-pbi-kpi-block" style={{ color: iashScoreColor(selectedArea ? iashAreaScore : kpis?.iash_average) }}>
                                <div className="db-pbi-kpi-value">{formatRounded(selectedArea ? iashAreaScore : kpis?.iash_average)}</div>
                                <div className="db-pbi-kpi-label">IASH · Autocuidado</div>
                            </div>
                        </div>
                        <div className="db-pbi-filter">
                            <span className="selfcare-managerial-label">DNI Evaluado</span>
                            <Select
                                showSearch
                                allowClear
                                placeholder="Todos los evaluados"
                                value={selectedIashDni}
                                onChange={(val) => { setSelectedIashDni(val ?? null); if (!val) setIashPersonaData(null); }}
                                options={iashDniOptions}
                                style={{ minWidth: 300 }}
                                optionFilterProp="label"
                                loading={iashPersonaLoading}
                            />
                        </div>
                    </div>
                    {/* Item detail table */}
                    <Card title={selectedIashDni && iashPersonaData ? `Resultado IASH · ${iashPersonaData.name || selectedIashDni}` : `Resultado IASH por Ítem${selectedArea ? ` · ${selectedArea}` : ' · Todos'}`}>
                        <Spin spinning={iashPersonaLoading}>
                        {iashItemRows.length > 0 ? (
                            <table className="db-item-table">
                                <thead>
                                    <tr>
                                        <th className="db-item-col-dim">Dimensión</th>
                                        <th className="db-item-col-score">Puntaje Autocuidado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {iashItemRows.map((row) => (
                                        <tr key={row.id} className={row.isGroup ? 'db-item-row-group' : 'db-item-row-item'}>
                                            <td className="db-item-cell-label">
                                                {row.isGroup
                                                    ? <strong>{row.label}</strong>
                                                    : <span className="db-item-question">{row.label}</span>
                                                }
                                            </td>
                                            <td className="db-item-cell-score"
                                                style={{ color: row.average != null ? iashScoreColor(row.average) : undefined }}>
                                                {row.average != null ? (Math.round(row.average * 100) / 100).toFixed(2) : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                    <tr className="db-item-row-total">
                                        <td><strong>Total</strong></td>
                                        {(() => { const tot = selectedIashDni ? iashPersonaData?.average : (selectedArea ? iashAreaScore : kpis?.iash_average); return (
                                        <td className="db-item-cell-score" style={{ color: iashScoreColor(tot) }}>
                                            <strong>{formatRounded(tot)}</strong>
                                        </td>); })()}
                                    </tr>
                                </tbody>
                            </table>
                        ) : (
                            <Empty description="Sin datos de ítems disponibles" />
                        )}
                        </Spin>
                    </Card>
                </div>
            ),
        },
        {
            key: 'ipra',
            label: 'IPRA · Visión 360',
            children: (
                <div className="managerial-dashboard-tab-content">
                    {/* Toolbar: KPIs + filtro Gerencia */}
                    <div className="db-pbi-toolbar">
                        <div className="db-pbi-kpis">
                            <div className="db-pbi-kpi-block">
                                <div className="db-pbi-kpi-value">{ipraEvaluatedCount}</div>
                                <div className="db-pbi-kpi-label">Personas Evaluadas</div>
                            </div>
                            <div className="db-pbi-kpi-block" style={{ color: ipraScoreColor(selectedArea ? ipraAreaScore : kpis?.ipra_average) }}>
                                <div className="db-pbi-kpi-value">{formatRounded(selectedArea ? ipraAreaScore : kpis?.ipra_average)}</div>
                                <div className="db-pbi-kpi-label">IPRA · Liderazgo</div>
                            </div>
                            <div className="db-pbi-kpi-block">
                                <div className="db-pbi-kpi-value" style={{ color: countBelowThreshold(filteredIpraRows, 75) > 0 ? '#E74C3C' : '#62C190' }}>
                                    {countBelowThreshold(filteredIpraRows, 75)} / {filteredIpraRows.length}
                                </div>
                                <div className="db-pbi-kpi-label">Dim. bajo umbral (75)</div>
                            </div>
                        </div>
                        {selectedArea && (
                            <div className="db-pbi-filter">
                                <Button
                                    size="small"
                                    icon={<SnippetsOutlined />}
                                    onClick={() => { setAreaReportTarget(selectedArea); setAreaReportOpen(true); }}
                                >
                                    Informe · {selectedArea}
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Fila 1: por Dimensión (línea) + por Gerencia */}
                    <Row gutter={[16, 16]} align="top">
                        <Col xs={24} xl={12}>
                            <Card title={`Puntaje IPRA por Dimensión${selectedArea ? ` · ${selectedArea}` : ''}`}>
                                <DimensionLineChart rows={filteredIpraRows} instrument="ipra" />
                            </Card>
                        </Col>
                        <Col xs={24} xl={12}>
                            <Card title="IPRA · Liderazgo por Gerencia">
                                <BreakdownBarChart
                                    rows={ipraMgmtAreaRows}
                                    instrument="ipra"
                                    selectedName={selectedArea}
                                    onSelect={(name) => setSelectedArea(name === selectedArea ? null : name)}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Fila 2: por Área + por Cargo */}
                    <Row gutter={[16, 16]} align="top">
                        <Col xs={24} xl={12}>
                            <Card title={`IPRA por Área${selectedArea ? ` · ${selectedArea}` : ''}`}>
                                <BreakdownBarChart rows={ipraOpAreaRows} instrument="ipra" maxItems={15} />
                            </Card>
                        </Col>
                        <Col xs={24} xl={12}>
                            <Card title={`IPRA por Cargo${selectedArea ? ` · ${selectedArea}` : ''}`}>
                                <BreakdownBarChart rows={ipraJobTitleRows} instrument="ipra" maxItems={15} />
                            </Card>
                        </Col>
                    </Row>

                    {/* Fila 3: por Categoría de Cargo + cobertura si hay área */}
                    <Row gutter={[16, 16]} align="top">
                        <Col xs={24} xl={selectedArea ? 12 : 24}>
                            <Card title={`IPRA por Categoría de Cargo${selectedArea ? ` · ${selectedArea}` : ''}`}>
                                <BreakdownBarChart rows={ipraJobCategoryRows} instrument="ipra" />
                            </Card>
                        </Col>
                        {selectedArea && (
                            <Col xs={24} xl={12}>
                                <Card title={`Cobertura · ${selectedArea}`}>
                                    <CoveragePanel
                                        coverage={null}
                                        areaScore={ipraAreaScore}
                                        breachCount={countBelowThreshold(filteredIpraRows, 75)}
                                        totalDims={filteredIpraRows.length}
                                        instrument="ipra"
                                    />
                                </Card>
                            </Col>
                        )}
                    </Row>

                    {/* Tabla de supervisores (cuando hay gerencia seleccionada) */}
                    {selectedArea && areaSupervisors.length > 0 && (
                        <Row gutter={[16, 16]}>
                            <Col xs={24}>
                                <Card
                                    size="small"
                                    title={`Supervisores del área · ${selectedArea} (${areaSupervisors.length})`}
                                >
                                    <Table
                                        rowKey="codigo"
                                        dataSource={areaSupervisors.slice(0, 10)}
                                        size="small"
                                        pagination={false}
                                        scroll={{ x: 'max-content' }}
                                        columns={[
                                            { title: 'Código', dataIndex: 'codigo', key: 'codigo', width: 90 },
                                            {
                                                title: 'IPRA', dataIndex: 'ipra_score', key: 'ipra', width: 72, align: 'center',
                                                render: (v) => v != null ? (
                                                    <span style={{ fontWeight: 800, color: getDimBarColor(v, 'ipra') }}>
                                                        {Math.round(v * 10) / 10}
                                                    </span>
                                                ) : '—',
                                            },
                                            {
                                                title: 'Dimensión crítica', dataIndex: 'critical_dimension', key: 'cd',
                                                render: (v) => v ? String(v).split('/')[0].trim() : '—',
                                                ellipsis: true,
                                            },
                                            { title: 'N° cargo', dataIndex: 'n_cargo', key: 'ncargo', width: 82, align: 'center' },
                                            {
                                                title: 'Exposición', dataIndex: 'exposicion', key: 'expo', width: 105, align: 'center',
                                                render: (v) => {
                                                    const colorMap = { EXTREMA: 'error', ALTA: 'warning', MEDIA: 'gold', BAJA: 'success' };
                                                    const c = colorMap[(v || '').toUpperCase()] || 'default';
                                                    return v ? <Tag color={c} style={{ margin: 0 }}>{v}</Tag> : '—';
                                                },
                                            },
                                            {
                                                title: 'Grupo', dataIndex: 'grupo', key: 'grupo', width: 75, align: 'center',
                                                render: (v) => v ? (
                                                    <Tag color={GRUPO_DASH_COLOR[(v || '').toUpperCase()] || 'default'} style={{ margin: 0 }}>{v}</Tag>
                                                ) : '—',
                                            },
                                        ]}
                                    />
                                </Card>
                            </Col>
                        </Row>
                    )}
                </div>
            ),
        },
        {
            key: 'ipra-personas',
            label: 'IPRA · Personas',
            children: (
                <div className="managerial-dashboard-tab-content">
                    {/* Toolbar */}
                    <div className="db-pbi-toolbar">
                        <div className="db-pbi-kpis">
                            <div className="db-pbi-kpi-block">
                                <div className="db-pbi-kpi-value">{ipraEvaluatedCount}</div>
                                <div className="db-pbi-kpi-label">Personas Evaluadas</div>
                            </div>
                            <div className="db-pbi-kpi-block" style={{ color: ipraScoreColor(selectedArea ? ipraAreaScore : kpis?.ipra_average) }}>
                                <div className="db-pbi-kpi-value">{formatRounded(selectedArea ? ipraAreaScore : kpis?.ipra_average)}</div>
                                <div className="db-pbi-kpi-label">IPRA · Liderazgo</div>
                            </div>
                        </div>
                        <div className="db-pbi-filter">
                            <span className="selfcare-managerial-label">DNI Evaluado</span>
                            <Select
                                showSearch
                                allowClear
                                placeholder="Todos los evaluados"
                                value={selectedIpraDni}
                                onChange={(val) => { setSelectedIpraDni(val ?? null); if (!val) setIpraPersonaData(null); }}
                                options={ipraDniOptions}
                                style={{ minWidth: 300 }}
                                optionFilterProp="label"
                                loading={ipraPersonaLoading}
                            />
                        </div>
                    </div>
                    {/* Item detail table */}
                    <Card title={selectedIpraDni && ipraPersonaData ? `Resultado IPRA · ${ipraPersonaData.name || selectedIpraDni}` : `Resultado IPRA por Ítem${selectedArea ? ` · ${selectedArea}` : ' · Todos'}`}>
                        <Spin spinning={ipraPersonaLoading}>
                        {ipraItemRows.length > 0 ? (
                            <table className="db-item-table">
                                <thead>
                                    <tr>
                                        <th className="db-item-col-dim">Dimensión</th>
                                        <th className="db-item-col-score">Puntaje Visión 360</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ipraItemRows.map((row) => (
                                        <tr key={row.id} className={row.isGroup ? 'db-item-row-group' : 'db-item-row-item'}>
                                            <td className="db-item-cell-label">
                                                {row.isGroup
                                                    ? <strong>{row.label}</strong>
                                                    : <span className="db-item-question">{row.label}</span>
                                                }
                                            </td>
                                            <td className="db-item-cell-score"
                                                style={{ color: row.average != null ? ipraScoreColor(row.average) : undefined }}>
                                                {row.average != null ? (Math.round(row.average * 100) / 100).toFixed(2) : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                    <tr className="db-item-row-total">
                                        <td><strong>Total</strong></td>
                                        {(() => { const tot = selectedIpraDni ? ipraPersonaData?.average : (selectedArea ? ipraAreaScore : kpis?.ipra_average); return (
                                        <td className="db-item-cell-score" style={{ color: ipraScoreColor(tot) }}>
                                            <strong>{formatRounded(tot)}</strong>
                                        </td>); })()}
                                    </tr>
                                </tbody>
                            </table>
                        ) : (
                            <Empty description="Sin datos de ítems disponibles" />
                        )}
                        </Spin>
                    </Card>
                </div>
            ),
        },
        {
            key: 'nps',
            label: 'NPS',
            children: (
                <div className="managerial-dashboard-tab-content">
                    {npsData ? (
                        <>
                            {/* Toolbar */}
                            <div className="db-pbi-toolbar">
                                <div className="db-pbi-kpis">
                                    <div className="db-pbi-kpi-block" style={{ color: getNpsBarColor(npsData.score) }}>
                                        <div className="db-pbi-kpi-value">{formatRounded(npsData.score)}</div>
                                        <div className="db-pbi-kpi-label">NPS Seguridad</div>
                                    </div>
                                    <div className="db-pbi-kpi-block">
                                        <div className="db-pbi-kpi-value">{npsData.total ?? 0}</div>
                                        <div className="db-pbi-kpi-label">Personas Evaluadas</div>
                                    </div>
                                    {npsData.promoters_pct != null && (
                                        <div className="db-pbi-kpi-block">
                                            <div className="db-pbi-kpi-value" style={{ color: '#1E8449' }}>{npsData.promoters_pct}%</div>
                                            <div className="db-pbi-kpi-label">Promotores</div>
                                        </div>
                                    )}
                                    {npsData.passives_pct != null && (
                                        <div className="db-pbi-kpi-block">
                                            <div className="db-pbi-kpi-value" style={{ color: '#D4AC0D' }}>{npsData.passives_pct}%</div>
                                            <div className="db-pbi-kpi-label">Pasivos</div>
                                        </div>
                                    )}
                                    {npsData.detractors_pct != null && (
                                        <div className="db-pbi-kpi-block">
                                            <div className="db-pbi-kpi-value" style={{ color: '#E74C3C' }}>{npsData.detractors_pct}%</div>
                                            <div className="db-pbi-kpi-label">Detractores</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {/* Semáforo NPS */}
                            <div className="db-nps-legend">
                                {[
                                    { range: '> 70', label: 'Excelente', color: '#1E8449' },
                                    { range: '50 – 70', label: 'Bueno', color: '#62C190' },
                                    { range: '20 – 49', label: 'Moderado', color: '#D4AC0D' },
                                    { range: '0 – 19', label: 'Bajo', color: '#E67E22' },
                                    { range: '< 0', label: 'Crítico', color: '#E74C3C' },
                                ].map((item) => (
                                    <span key={item.label} className="db-nps-legend-item">
                                        <span className="db-nps-legend-dot" style={{ background: item.color }} />
                                        <span className="db-nps-legend-range">{item.range}</span>
                                        <span className="db-nps-legend-label">{item.label}</span>
                                    </span>
                                ))}
                            </div>
                            {npsData.insight && (
                                <Alert type="info" showIcon message={npsData.insight} style={{ marginBottom: 16 }} />
                            )}
                            <Row gutter={[16, 16]}>
                                <Col xs={24} xl={12}>
                                    <Card title="NPS por Gerencia">
                                        <NpsBarChart
                                            rows={npsByGerencia}
                                            selectedName={selectedArea}
                                            onSelect={(name) => setSelectedArea(name === selectedArea ? null : name)}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} xl={12}>
                                    <Card title="NPS por Área">
                                        <NpsBarChart rows={npsByOpArea} maxItems={15} />
                                    </Card>
                                </Col>
                            </Row>
                            <Row gutter={[16, 16]}>
                                <Col xs={24} xl={12}>
                                    <Card title="NPS por Categoría de Cargo">
                                        <NpsBarChart rows={npsByJobCategory} />
                                    </Card>
                                </Col>
                                <Col xs={24} xl={12}>
                                    <Card title="NPS por Cargo">
                                        <NpsBarChart rows={npsByJobTitle} maxItems={15} />
                                    </Card>
                                </Col>
                            </Row>
                        </>
                    ) : (
                        <Empty description="No hay datos NPS disponibles para este cruce" style={{ padding: '40px 0' }} />
                    )}
                </div>
            ),
        },
        {
            key: 'nps-personas',
            label: 'NPS · Personas',
            children: (
                <div className="managerial-dashboard-tab-content">
                    <div className="db-pbi-toolbar">
                        <div className="db-pbi-kpis">
                            {npsData && (
                                <div className="db-pbi-kpi-block" style={{ color: getNpsBarColor(npsData.score) }}>
                                    <div className="db-pbi-kpi-value">{formatRounded(npsData.score)}</div>
                                    <div className="db-pbi-kpi-label">NPS Seguridad</div>
                                </div>
                            )}
                            <div className="db-pbi-kpi-block">
                                <div className="db-pbi-kpi-value">{npsEmployees.length}</div>
                                <div className="db-pbi-kpi-label">Supervisores</div>
                            </div>
                        </div>
                    </div>
                    <Table
                        rowKey="dni"
                        dataSource={npsEmployees}
                        size="small"
                        pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
                        scroll={{ x: 'max-content' }}
                        columns={[
                            {
                                title: 'Cargo',
                                dataIndex: 'job_title',
                                key: 'job_title',
                                width: 200,
                                sorter: (a, b) => (a.job_title || '').localeCompare(b.job_title || ''),
                            },
                            {
                                title: 'Nombre',
                                dataIndex: 'name',
                                key: 'name',
                                width: 220,
                                sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
                            },
                            {
                                title: 'DNI Evaluado',
                                dataIndex: 'dni',
                                key: 'dni',
                                width: 130,
                            },
                            {
                                title: 'Gerencia',
                                dataIndex: 'management_area',
                                key: 'management_area',
                                width: 180,
                            },
                            {
                                title: 'NPS',
                                dataIndex: 'nps',
                                key: 'nps',
                                width: 90,
                                align: 'center',
                                sorter: (a, b) => (a.nps ?? -200) - (b.nps ?? -200),
                                defaultSortOrder: 'descend',
                                render: (v) => v != null
                                    ? <span style={{ fontWeight: 700, color: getNpsBarColor(v) }}>{v}</span>
                                    : '—',
                            },
                            {
                                title: 'Promotores',
                                dataIndex: 'promoters',
                                key: 'promoters',
                                width: 100,
                                align: 'center',
                                render: (v) => <span style={{ color: '#1E8449', fontWeight: 600 }}>{v ?? 0}</span>,
                            },
                            {
                                title: 'Pasivos',
                                dataIndex: 'passives',
                                key: 'passives',
                                width: 80,
                                align: 'center',
                            },
                            {
                                title: 'Detractores',
                                dataIndex: 'detractors',
                                key: 'detractors',
                                width: 100,
                                align: 'center',
                                render: (v) => v > 0
                                    ? <span style={{ color: '#E74C3C', fontWeight: 600 }}>{v}</span>
                                    : (v ?? 0),
                            },
                            {
                                title: 'Total',
                                dataIndex: 'total',
                                key: 'total',
                                width: 70,
                                align: 'center',
                            },
                        ]}
                    />
                </div>
            ),
        },
        {
            key: 'actions',
            label: 'Gestión de informes',
            children: (
                <div className="managerial-dashboard-tab-content">
                    <Row gutter={[16, 16]}>
                        <Col xs={24} xl={24}>
                            <Card title="Acciones disponibles" className="managerial-dashboard-fill-card">
                                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                    <Button icon={<FileTextOutlined />} onClick={onOpenFullReport} block>
                                        Abrir informe paginado
                                    </Button>
                                    <Button type="primary" icon={<DownloadOutlined />} onClick={() => handleExport('download')} loading={exportLoading} block>
                                        Descargar informe PDF
                                    </Button>
                                    {selectedArea && (
                                        <Button
                                            icon={<SnippetsOutlined />}
                                            onClick={() => { setAreaReportTarget(selectedArea); setAreaReportOpen(true); }}
                                            block
                                        >
                                            Informe de Área — {selectedArea}
                                        </Button>
                                    )}
                                </Space>
                            </Card>
                        </Col>
                        {/* <Col xs={24} xl={14}>
                            <Card title="Siguientes iteraciones" className="managerial-dashboard-fill-card">
                                <List
                                    dataSource={[
                                        'Conectar cada pestaña con drill-down al mismo nivel de detalle de las páginas del informe.',
                                        'Agregar filtros por área, comparación entre corridas y trazabilidad temporal del ICP.',
                                        'Añadir marcadores de seguimiento y exportaciones complementarias para comité gerencial.',
                                    ]}
                                    renderItem={(item) => <List.Item>{item}</List.Item>}
                                />
                            </Card>
                        </Col> */}
                    </Row>
                </div>
            ),
        },
        {
            key: 'collaborators',
            label: 'Colaboradores',
            children: (
                <div className="managerial-dashboard-tab-content">
                    <Table
                        rowKey="dni"
                        dataSource={collaboratorsFiltered}
                        size="small"
                        scroll={{ x: true }}
                        pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10','20','50'] }}
                        onChange={(_, filters) => setCollabFilters(filters)}
                        expandable={{
                            expandedRowRender: (record) => {
                                const empAcc = accidents.filter((a) => String(a.acc_employee_dni) === String(record.dni));
                                const empAbs = absences.filter((a) => String(a.abs_employee_dni) === String(record.dni));
                                if (empAcc.length === 0 && empAbs.length === 0) {
                                    return <Empty description="Sin accidentes ni ausentismos registrados" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: '8px 0' }} />;
                                }
                                return (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 0' }}>
                                        {empAcc.length > 0 && (
                                            <div>
                                                <div style={{ fontWeight: 600, marginBottom: 6 }}><SafetyOutlined style={{ marginRight: 4, color: '#E67E22' }} />Accidentes ({empAcc.length})</div>
                                                <Table rowKey="acc_id" dataSource={empAcc} size="small" pagination={false} columns={[
                                                    { title: 'Fecha', dataIndex: 'acc_event_date', width: 100 },
                                                    { title: 'Tipo', dataIndex: 'acc_event_type', width: 120 },
                                                    { title: 'Mecanismo', dataIndex: 'acc_mechanism', width: 160 },
                                                    { title: 'Parte afectada', dataIndex: 'acc_body_part', width: 140 },
                                                    { title: 'Días perdidos', dataIndex: 'acc_days_lost', width: 100, align: 'center' },
                                                    { title: 'Hospitalizado', dataIndex: 'acc_hospitalized', width: 100, align: 'center', render: (v) => v ? <Tag color="red">Sí</Tag> : 'No' },
                                                    { title: 'Gravedad', dataIndex: 'acc_severity', width: 100 },
                                                ]} />
                                            </div>
                                        )}
                                        {empAbs.length > 0 && (
                                            <div>
                                                <div style={{ fontWeight: 600, marginBottom: 6 }}><SafetyOutlined style={{ marginRight: 4, color: '#1E8449' }} />Ausentismos ({empAbs.length})</div>
                                                <Table rowKey="abs_id" dataSource={empAbs} size="small" pagination={false} columns={[
                                                    { title: 'Fecha inicio', dataIndex: 'abs_start_date', width: 110 },
                                                    { title: 'Fecha fin', dataIndex: 'abs_end_date', width: 110 },
                                                    { title: 'Tipo', dataIndex: 'abs_absence_type', width: 140 },
                                                    { title: 'Días perdidos', dataIndex: 'abs_days_lost', width: 100, align: 'center' },
                                                    { title: 'Diagnóstico', dataIndex: 'abs_diagnosis_category', width: 160 },
                                                    { title: 'Recurrente', dataIndex: 'abs_recurrent', width: 90, align: 'center', render: (v) => v ? <Tag color="orange">Sí</Tag> : 'No' },
                                                ]} />
                                            </div>
                                        )}
                                    </div>
                                );
                            },
                            rowExpandable: (record) =>
                                accidents.some((a) => String(a.acc_employee_dni) === String(record.dni)) ||
                                absences.some((a) => String(a.abs_employee_dni) === String(record.dni)),
                        }}
                        columns={[
                            { title: 'DNI', dataIndex: 'dni', key: 'dni', width: 110, sorter: (a, b) => String(a.dni).localeCompare(String(b.dni)), ...getColumnSearchProps('dni', 'DNI') },
                            { title: 'Apellidos', dataIndex: 'last_name', key: 'last_name', width: 160, sorter: (a, b) => (a.last_name || '').localeCompare(b.last_name || ''), ...getColumnSearchProps('last_name', 'apellidos') },
                            { title: 'Nombres', dataIndex: 'first_name', key: 'first_name', width: 140, sorter: (a, b) => (a.first_name || '').localeCompare(b.first_name || ''), ...getColumnSearchProps('first_name', 'nombres') },
                            {
                                title: 'Rol', dataIndex: 'role', key: 'role', width: 110,
                                sorter: (a, b) => (a.role || '').localeCompare(b.role || ''),
                                filters: [{ text: 'Líder', value: 'Líder' }, { text: 'Colaborador', value: 'Colaborador' }],
                                filteredValue: collabFilters.role || null,
                                onFilter: (value, record) => record.role === value,
                                render: (v) => v === 'Líder'
                                    ? <Tag color="blue">Líder</Tag>
                                    : <Tag color="default">Colaborador</Tag>,
                            },
                            { title: 'Gerencia', dataIndex: 'management_area', key: 'management_area', width: 170,
                                sorter: (a, b) => (a.management_area || '').localeCompare(b.management_area || ''),
                                filters: empGerenciaOptions.map((o) => ({ text: o, value: o })),
                                filteredValue: collabFilters.management_area || null,
                                filterSearch: true,
                                onFilter: (value, record) => record.management_area === value,
                            },
                            { title: 'Área', dataIndex: 'area', key: 'area', width: 150,
                                sorter: (a, b) => (a.area || '').localeCompare(b.area || ''),
                                filters: empAreaOptions.map((o) => ({ text: o, value: o })),
                                filteredValue: collabFilters.area || null,
                                filterSearch: true,
                                onFilter: (value, record) => record.area === value,
                            },
                            { title: 'Sede', dataIndex: 'sede', key: 'sede', width: 130,
                                sorter: (a, b) => (a.sede || '').localeCompare(b.sede || ''),
                                filters: empSedeOptions.map((o) => ({ text: o, value: o })),
                                filteredValue: collabFilters.sede || null,
                                filterSearch: true,
                                onFilter: (value, record) => record.sede === value,
                            },
                            {
                                title: 'IPRA', dataIndex: 'ipra_score', key: 'ipra_score', width: 80, align: 'center',
                                sorter: (a, b) => (a.ipra_score ?? -1) - (b.ipra_score ?? -1),
                                render: (v) => v != null ? <span style={{ color: ipraScoreColor(v), fontWeight: 600 }}>{v}</span> : <span style={{ color: '#aaa' }}>—</span>,
                            },
                            {
                                title: 'IASH', dataIndex: 'iash_score', key: 'iash_score', width: 80, align: 'center',
                                sorter: (a, b) => (a.iash_score ?? -1) - (b.iash_score ?? -1),
                                render: (v) => v != null ? <span style={{ color: iashScoreColor(v), fontWeight: 600 }}>{v}</span> : <span style={{ color: '#aaa' }}>—</span>,
                            },
                            {
                                title: 'Accidentalidad', key: 'acc', width: 110, align: 'center',
                                sorter: (a, b) =>
                                    accidents.filter((x) => String(x.acc_employee_dni) === String(a.dni)).length -
                                    accidents.filter((x) => String(x.acc_employee_dni) === String(b.dni)).length,
                                render: (_, record) => {
                                    const n = accidents.filter((x) => String(x.acc_employee_dni) === String(record.dni)).length;
                                    return n > 0 ? <Tag color="red">{n}</Tag> : <Tag>0</Tag>;
                                },
                            },
                            {
                                title: 'Ausentismos', key: 'abs', width: 110, align: 'center',
                                sorter: (a, b) =>
                                    absences.filter((x) => String(x.abs_employee_dni) === String(a.dni)).length -
                                    absences.filter((x) => String(x.abs_employee_dni) === String(b.dni)).length,
                                render: (_, record) => {
                                    const n = absences.filter((x) => String(x.abs_employee_dni) === String(record.dni)).length;
                                    return n > 0 ? <Tag color="orange">{n}</Tag> : <Tag>0</Tag>;
                                },
                            },
                        ]}
                    />
                </div>
            ),
        },
        {
                    key: 'accidents',
            label: 'Accidentes',
            children: (
                <div className="managerial-dashboard-tab-content">
                    <Spin spinning={safetyLoading}>
                        <Collapse
                            size="small"
                            style={{ marginBottom: 16 }}
                            defaultActiveKey={accidents.length === 0 ? ['upload'] : []}
                            items={[{
                                key: 'upload',
                                label: 'Cargar datos de seguridad (Excel)',
                                children: (
                                    <SafetyUploader
                                        loading={safetyUploadLoading}
                                        result={uploadResult}
                                        onFile={handleSafetyFileDrop}
                                    />
                                ),
                            }]}
                        />

                        {accidentsFiltered.length === 0 ? (
                            <Empty description={accidents.length === 0 ? 'No hay registros de accidentes para este cruce' : 'Sin registros para el área seleccionada'} />
                        ) : (
                            <>
                                <Row gutter={[16, 16]} justify="center" style={{ marginBottom: 16 }}>
                                    <Col xs={8} style={{ textAlign: 'center' }}>
                                        <Statistic title="Total eventos" value={accidentsFiltered.length} prefix={<SafetyOutlined />} />
                                    </Col>
                                    <Col xs={8} style={{ textAlign: 'center' }}>
                                        <Statistic title="Días perdidos" value={accidentsFiltered.reduce((s, r) => s + (r.acc_days_lost || 0), 0)} />
                                    </Col>
                                    <Col xs={8} style={{ textAlign: 'center' }}>
                                        <Statistic title="Con hospitalización" value={accidentsFiltered.filter((r) => r.acc_hospitalized).length} />
                                    </Col>
                                </Row>
                                <Table
                                    rowKey="acc_id"
                                    dataSource={accidentsFiltered}
                                    size="small"
                                    scroll={{ x: true }}
                                    pagination={{ pageSize: 10 }}
                                    columns={[
                                        { title: 'ID Evento', dataIndex: 'acc_event_id', key: 'acc_event_id', width: 110 },
                                        { title: 'Fecha', dataIndex: 'acc_event_date', key: 'acc_event_date', width: 100 },
                                        { title: 'Tipo', dataIndex: 'acc_event_type', key: 'acc_event_type', width: 120 },
                                        { title: 'DNI', dataIndex: 'acc_employee_dni', key: 'acc_employee_dni', width: 100 },
                                        { title: 'Empleado', dataIndex: 'employee_name', key: 'employee_name', width: 160, render: (v) => v || <Tag color="orange">No vinculado</Tag> },
                                        { title: 'Área', dataIndex: 'employee_area', key: 'employee_area', width: 130 },
                                        { title: 'Gerencia', dataIndex: 'employee_management_area', key: 'employee_management_area', width: 140 },
                                        { title: 'IPRA', key: 'ipra', width: 70, align: 'center', render: (_, r) => { const s = scoresByDni[String(r.acc_employee_dni)]?.ipra; return s != null ? <span style={{ color: ipraScoreColor(s), fontWeight: 600 }}>{s}</span> : '—'; } },
                                        { title: 'IASH', key: 'iash', width: 70, align: 'center', render: (_, r) => { const s = scoresByDni[String(r.acc_employee_dni)]?.iash; return s != null ? <span style={{ color: iashScoreColor(s), fontWeight: 600 }}>{s}</span> : '—'; } },
                                        { title: 'Mecanismo', dataIndex: 'acc_mechanism', key: 'acc_mechanism', width: 160 },
                                        { title: 'Parte afectada', dataIndex: 'acc_body_part', key: 'acc_body_part', width: 140 },
                                        { title: 'Días perdidos', dataIndex: 'acc_days_lost', key: 'acc_days_lost', width: 110, align: 'center' },
                                        { title: 'Hospitalizado', dataIndex: 'acc_hospitalized', key: 'acc_hospitalized', width: 110, align: 'center', render: (v) => v ? <Tag color="red">Sí</Tag> : <Tag>No</Tag> },
                                        { title: 'Gravedad', dataIndex: 'acc_severity', key: 'acc_severity', width: 110 },
                                    ]}
                                />
                            </>
                        )}
                    </Spin>
                </div>
            ),
        },
        {
                    key: 'absences',
            label: 'Ausentismos',
            children: (
                <div className="managerial-dashboard-tab-content">
                    <Spin spinning={safetyLoading}>
                        <Collapse
                            size="small"
                            style={{ marginBottom: 16 }}
                            defaultActiveKey={absences.length === 0 ? ['upload'] : []}
                            items={[{
                                key: 'upload',
                                label: 'Cargar datos de seguridad (Excel)',
                                children: (
                                    <SafetyUploader
                                        loading={safetyUploadLoading}
                                        result={uploadResult}
                                        onFile={handleSafetyFileDrop}
                                    />
                                ),
                            }]}
                        />

                        {absencesFiltered.length === 0 ? (
                            <Empty description={absences.length === 0 ? 'No hay registros de ausentismos para este cruce' : 'Sin registros para el área seleccionada'} />
                        ) : (
                            <>
                                <Row gutter={[16, 16]} justify="center" style={{ marginBottom: 16 }}>
                                    <Col xs={8} style={{ textAlign: 'center' }}>
                                        <Statistic title="Total registros" value={absencesFiltered.length} prefix={<SafetyOutlined />} />
                                    </Col>
                                    <Col xs={8} style={{ textAlign: 'center' }}>
                                        <Statistic title="Días perdidos" value={absencesFiltered.reduce((s, r) => s + (r.abs_days_lost || 0), 0)} />
                                    </Col>
                                    <Col xs={8} style={{ textAlign: 'center' }}>
                                        <Statistic title="Recurrentes" value={absencesFiltered.filter((r) => r.abs_recurrent).length} />
                                    </Col>
                                </Row>
                                <Table
                                    rowKey="abs_id"
                                    dataSource={absencesFiltered}
                                    size="small"
                                    scroll={{ x: true }}
                                    pagination={{ pageSize: 10 }}
                                    columns={[
                                        // { title: 'ID Registro', dataIndex: 'abs_record_id', key: 'abs_record_id', width: 110 },
                                        { title: 'DNI', dataIndex: 'abs_employee_dni', key: 'abs_employee_dni', width: 100 },
                                        { title: 'Empleado', dataIndex: 'employee_name', key: 'employee_name', width: 160, render: (v) => v || <Tag color="orange">No vinculado</Tag> },
                                        { title: 'Área', dataIndex: 'employee_area', key: 'employee_area', width: 130 },
                                        { title: 'Gerencia', dataIndex: 'employee_management_area', key: 'employee_management_area', width: 140 },
                                        { title: 'IPRA', key: 'ipra', width: 70, align: 'center', render: (_, r) => { const s = scoresByDni[String(r.abs_employee_dni)]?.ipra; return s != null ? <span style={{ color: ipraScoreColor(s), fontWeight: 600 }}>{s}</span> : '—'; } },
                                        { title: 'IASH', key: 'iash', width: 70, align: 'center', render: (_, r) => { const s = scoresByDni[String(r.abs_employee_dni)]?.iash; return s != null ? <span style={{ color: iashScoreColor(s), fontWeight: 600 }}>{s}</span> : '—'; } },
                                        { title: 'Tipo ausencia', dataIndex: 'abs_absence_type', key: 'abs_absence_type', width: 140 },
                                        { title: 'Fecha inicio', dataIndex: 'abs_start_date', key: 'abs_start_date', width: 110 },
                                        { title: 'Fecha fin', dataIndex: 'abs_end_date', key: 'abs_end_date', width: 110 },
                                        { title: 'Días perdidos', dataIndex: 'abs_days_lost', key: 'abs_days_lost', width: 110, align: 'center' },
                                        { title: 'Diagnóstico', dataIndex: 'abs_diagnosis_category', key: 'abs_diagnosis_category', width: 160 },
                                        { title: 'Recurrente', dataIndex: 'abs_recurrent', key: 'abs_recurrent', width: 100, align: 'center', render: (v) => v ? <Tag color="orange">Sí</Tag> : <Tag>No</Tag> },
                                    ]}
                                />
                            </>
                        )}
                    </Spin>
                </div>
            ),
        },
    ];

    return (
        <div className="managerial-dashboard-shell">
            <div className="managerial-dashboard-header">
                <div className="managerial-dashboard-header-copy">
                    <div className="managerial-dashboard-eyebrow">Dashboard gerencial</div>
                    <h3 className="managerial-dashboard-title">
                        {orgName ? `${orgName} · ` : ''}Cruce IPRA + IASH
                    </h3>
                    <p className="managerial-dashboard-subtitle">
                        {evaluations?.ipra?.title && evaluations?.iash?.title
                            ? `${evaluations.ipra.title}  ·  ${evaluations.iash.title}`
                            : 'Análisis predictivo de seguridad — liderazgo y autocuidado'}
                    </p>
                </div>

                <div className="managerial-dashboard-header-score">
                    <div className="managerial-dashboard-header-score-label">ICP Planta</div>
                    <div className="managerial-dashboard-header-score-value">
                        {formatRounded(kpis?.cross_index)}
                    </div>
                    <div className="managerial-dashboard-header-score-badge">
                        {getRiskLabel(kpis?.cross_index).toUpperCase()}
                    </div>
                </div>
            </div>

            {areaOptions.length > 0 && (
                <div className="managerial-dashboard-area-filter">
                    <span className="managerial-dashboard-area-filter-label">Filtrar por área:</span>
                    <Select
                        allowClear
                        placeholder="Todas las áreas"
                        options={areaOptions}
                        value={selectedArea}
                        onChange={(val) => setSelectedArea(val || null)}
                        style={{ minWidth: 220 }}
                        size="small"
                    />
                    {selectedArea && (
                        <span className="managerial-dashboard-area-filter-badge">
                            Área: {selectedArea}
                        </span>
                    )}
                </div>
            )}

            <div className="managerial-dashboard-tabs-wrap">
                <Tabs
                    items={tabItems}
                    activeKey={activeTab}
                    onChange={(key) => {
                        setActiveTab(key);
                        if (key !== 'iash-personas') { setSelectedIashDni(null); setIashPersonaData(null); }
                        if (key !== 'ipra-personas') { setSelectedIpraDni(null); setIpraPersonaData(null); }
                    }}
                />
            </div>

            <div className="managerial-dashboard-exporter" aria-hidden="true">
                <ManagerialCrossGeneral
                    ref={exporterRef}
                    reportData={reportData}
                    orgLogo={orgLogo}
                    orgName={orgName}
                    showFloatingActions={false}
                />
            </div>

            {/* ── Modal Informe de Área ── */}
            <Modal
                open={areaReportOpen}
                onCancel={() => setAreaReportOpen(false)}
                footer={[
                    <Button key="pdf-dl" type="primary" icon={<DownloadOutlined />}
                        onClick={() => areaReportRef.current?.downloadPdf()}>
                        Descargar PDF
                    </Button>,
                    <Button key="full-page" icon={<SnippetsOutlined />}
                        onClick={() => {
                            const gcrId = reportData?.gcr_id;
                            if (!gcrId || !areaReportTarget) return;
                            window.open(`/informes/area/${gcrId}/${encodeURIComponent(areaReportTarget)}`, '_blank');
                        }}
                    >
                        Abrir en página completa
                    </Button>,
                    <Button key="close" onClick={() => setAreaReportOpen(false)}>Cerrar</Button>,
                ]}
                title={`Informe de Área — ${areaReportTarget}`}
                width="95vw"
                style={{ top: 20 }}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', padding: 0 }}
                destroyOnClose
            >
                {areaReportTarget && (
                    <AreaReport
                        ref={areaReportRef}
                        reportData={reportData}
                        orgLogo={orgLogo}
                        orgName={orgName}
                        areaName={areaReportTarget}
                    />
                )}
            </Modal>

            {/* ── Preview modal ──────────────────────────────────────────────── */}
            <Modal
                open={previewOpen}
                title={`Previsualización — ${pendingFile?.name || 'archivo'}`}
                width="85vw"
                style={{ top: 20 }}
                onCancel={() => { setPreviewOpen(false); setPendingFile(null); setPreviewData(null); }}
                footer={[
                    <Button key="cancel" onClick={() => { setPreviewOpen(false); setPendingFile(null); setPreviewData(null); }}>
                        Cancelar
                    </Button>,
                    <Button
                        key="confirm"
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={confirmSafetyUpload}
                        disabled={!previewData || (
                            Math.max(0, (previewData.accidents?.new ?? 0) - (previewData.accidents?.unknown_employees ?? 0)) +
                            Math.max(0, (previewData.absences?.new ?? 0) - (previewData.absences?.unknown_employees ?? 0))
                        ) === 0}
                    >
                        Confirmar carga
                        {previewData ? ` (${
                            Math.max(0, (previewData.accidents?.new ?? 0) - (previewData.accidents?.unknown_employees ?? 0)) +
                            Math.max(0, (previewData.absences?.new ?? 0) - (previewData.absences?.unknown_employees ?? 0))
                        } se guardarán)` : ''}
                    </Button>,
                ]}
                destroyOnClose
            >
                {previewData && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        {previewData.errors?.length > 0 && (
                            <Alert type="error" showIcon message="Errores al leer el archivo" description={previewData.errors.join(' | ')} />
                        )}

                        {/* Accidentes summary */}
                        <Card
                            size="small"
                            title={<span><SafetyOutlined style={{ marginRight: 6 }} />Accidentes (hoja 5_ACCIDENTES)</span>}
                        >
                            <Row gutter={[16, 8]} style={{ marginBottom: 12 }}>
                                <Col xs={6}><Statistic title="Total en archivo" value={previewData.accidents?.total ?? 0} /></Col>
                                <Col xs={6}><Statistic title="Se guardarán" value={Math.max(0, (previewData.accidents?.new ?? 0) - (previewData.accidents?.unknown_employees ?? 0))} valueStyle={{ color: '#1E8449' }} /></Col>
                                <Col xs={6}><Statistic title="Duplicados (ya existen)" value={previewData.accidents?.duplicates ?? 0} valueStyle={{ color: '#888' }} /></Col>
                                <Col xs={6}><Statistic title="Sin empleado vinculado" value={previewData.accidents?.unknown_employees ?? 0} valueStyle={{ color: previewData.accidents?.unknown_employees > 0 ? '#E67E22' : undefined }} /></Col>
                            </Row>
                            {(previewData.accidents?.unknown_employees ?? 0) > 0 && (
                                <Alert type="warning" showIcon style={{ marginBottom: 10 }}
                                    message="Los registros sin empleado vinculado no serán guardados en la base de datos" />
                            )}
                            {previewData.accidents?.rows?.length > 0 && (
                                <Table
                                    rowKey="event_id"
                                    dataSource={previewData.accidents.rows}
                                    size="small"
                                    pagination={false}
                                    scroll={{ y: 220 }}
                                    rowClassName={(r) => (r.is_duplicate || !r.employee_found) ? 'safety-preview-row-dup' : ''}
                                    columns={[
                                        { title: 'Estado', key: 'status', width: 110, render: (_, r) => r.is_duplicate ? <Tag color="default">Duplicado</Tag> : !r.employee_found ? <Tag color="orange">Sin empleado</Tag> : <Tag color="green">Nuevo</Tag> },
                                        { title: 'ID Evento', dataIndex: 'event_id', key: 'event_id', width: 110 },
                                        { title: 'Fecha', dataIndex: 'date', key: 'date', width: 100 },
                                        { title: 'Tipo', dataIndex: 'type', key: 'type', width: 120 },
                                        { title: 'DNI', dataIndex: 'dni', key: 'dni', width: 100 },
                                        { title: 'Empleado', dataIndex: 'employee_name', key: 'employee_name', width: 160, render: (v) => v || <Tag color="orange">No vinculado</Tag> },
                                        { title: 'Área', dataIndex: 'area', key: 'area', width: 130 },
                                        { title: 'Días perdidos', dataIndex: 'days_lost', key: 'days_lost', width: 110, align: 'center' },
                                    ]}
                                />
                            )}
                        </Card>

                        {/* Ausentismos summary */}
                        <Card
                            size="small"
                            title={<span><SafetyOutlined style={{ marginRight: 6 }} />Ausentismos (hoja 6_AUSENTISMO)</span>}
                        >
                            <Row gutter={[16, 8]} style={{ marginBottom: 12 }}>
                                <Col xs={6}><Statistic title="Total en archivo" value={previewData.absences?.total ?? 0} /></Col>
                                <Col xs={6}><Statistic title="Se guardarán" value={Math.max(0, (previewData.absences?.new ?? 0) - (previewData.absences?.unknown_employees ?? 0))} valueStyle={{ color: '#1E8449' }} /></Col>
                                <Col xs={6}><Statistic title="Duplicados (ya existen)" value={previewData.absences?.duplicates ?? 0} valueStyle={{ color: '#888' }} /></Col>
                                <Col xs={6}><Statistic title="Sin empleado vinculado" value={previewData.absences?.unknown_employees ?? 0} valueStyle={{ color: previewData.absences?.unknown_employees > 0 ? '#E67E22' : undefined }} /></Col>
                            </Row>
                            {(previewData.absences?.unknown_employees ?? 0) > 0 && (
                                <Alert type="warning" showIcon style={{ marginBottom: 10 }}
                                    message="Los registros sin empleado vinculado no serán guardados en la base de datos" />
                            )}
                            {previewData.absences?.rows?.length > 0 && (
                                <Table
                                    rowKey="record_id"
                                    dataSource={previewData.absences.rows}
                                    size="small"
                                    pagination={false}
                                    scroll={{ y: 220 }}
                                    rowClassName={(r) => (r.is_duplicate || !r.employee_found) ? 'safety-preview-row-dup' : ''}
                                    columns={[
                                        { title: 'Estado', key: 'status', width: 110, render: (_, r) => r.is_duplicate ? <Tag color="default">Duplicado</Tag> : !r.employee_found ? <Tag color="orange">Sin empleado</Tag> : <Tag color="green">Nuevo</Tag> },
                                        { title: 'ID Registro', dataIndex: 'record_id', key: 'record_id', width: 110 },
                                        { title: 'Fecha inicio', dataIndex: 'start_date', key: 'start_date', width: 110 },
                                        { title: 'Tipo', dataIndex: 'type', key: 'type', width: 120 },
                                        { title: 'DNI', dataIndex: 'dni', key: 'dni', width: 100 },
                                        { title: 'Empleado', dataIndex: 'employee_name', key: 'employee_name', width: 160, render: (v) => v || <Tag color="orange">No vinculado</Tag> },
                                        { title: 'Área', dataIndex: 'area', key: 'area', width: 130 },
                                        { title: 'Días perdidos', dataIndex: 'days_lost', key: 'days_lost', width: 110, align: 'center' },
                                    ]}
                                />
                            )}
                        </Card>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default ManagerialCrossDashboard;